<script setup>
import { onMounted, onUnmounted, watch, ref, nextTick } from 'vue';
import { useStore } from 'vuex';
import { useRouter, useRoute } from 'vue-router';
import { canonicalUrl, applySeoMeta } from '@/utils/seo';

import FullCalendar from '@fullcalendar/vue3';
// import { format } from 'date-fns'; // [Remove] Not used
import { DEV_MODE } from '@/config/dev-mode';

/** API */
import { setMySajuAPI, getBestDayAPI, postChoiceLogAPI } from '@/api/calendarApi';
import { getAnalysisTicketAPI, getAnalysisLogAPI } from '@/api/AnalysisApi';
import { addContentLogAPI } from '@/api/logServiceApi';

/** pinia */
import { usePaymentsStore } from '@/store/modules/payments';
import { usePrimarySajuStore } from '@/store/modules/primarySaju';
import { useChoiceSubscriptionAccess } from '@/composables/choice/useChoiceSubscriptionAccess';

/** utils */
import { convertViewTitleToYYYYMM } from '@/utils/calendarUtil';
import { formatDate } from '@/utils/dateFormatter';
import {
  getChoiceRangeErrorMessage,
  isDateInChoiceRange,
  isDateBeforeRange,
} from '@/utils/choiceDateValidator';

/** composables */
import { useSnackbar } from '@/composables/useSnackbar';
import { useSajuClock } from '@/composables/calendar/useSajuClock';
import { useCalendar } from '@/composables/calendar/useCalendar';
import { useCalendarFilter } from '@/composables/calendar/useCalendarFilter';
import { useGesture } from '@/composables/calendar/useGesture';
import useFunctions from '@/composables/useFunctions';
import { useDialogManager } from '@/composables/useDialogManager';
import { useDeviceDetect } from '@/composables/useDeviceDetect';
import { useMySaju, clearMySajuState } from '@/composables/calendar/mySaju/useMySaju';
import { useMySajuValidator } from '@/composables/calendar/mySaju/useMySajuValidator';
import { useParsedMySajuInfo } from '@/composables/calendar/mySaju/useParsedMySajuInfo';
import { useCalendarValidation } from '@/composables/calendar/useCalendarValidation'; // import 확인
import { useBackLayer } from '@/composables/useAppBack';

import fiveMixin from '@/composables/fiveMixin';

/** 컴포넌트 */
import CreditBar from '@/components/calendar/CreditBar.vue';
import SajuClockPopup from '@/components/calendar/SajuClockPopup.vue';

import SelectionLnb from '@/components/selection/SelectionLnb.vue';
import SelectionHeader from '@/components/selection/SelectionHeader.vue';
// import ChoiceWeekPopup from '@/components/choice/popup/ChoiceWeekPopup.vue'; // [Remove] Unified into ChoiceDayPopup // [추가] 택일 주간 팝업
import ChoiceDatePopup from '@/components/choice/popup/ChooseDatePopupContainer.vue';
import ChoiceStartPopup from '@/components/choice/popup/ChoiceStartPopup.vue';
import ChoicePaymentPopup from '@/components/choice/popup/ChoicePaymentPopup.vue'; // [추가] 택일 결제 팝업
import ToastDatePopup from '@/components/choice/popup/ToastDatePopup.vue'; // [추가]
import LoadingIndicator from '@/components/common/LoadingIndicator.vue';
import Snackbar from '@/components/common/Snackbar.vue';
import AlertComponent from '@/components/common/Alert.vue';
import ConfirmComponent from '@/components/common/Confirm.vue';
import ChoiceConfirmPopup from '@/components/choice/popup/ChoiceConfirmPopup.vue'; // [추가]

/** [반응형] 모바일 전용 컴포넌트 */
import CalendarHeaderMobile from '@/components/calendar/CalendarHeaderMobile.vue';
import MobileHeader from '@/components/common/MobileHeader.vue';
import MobileCalendarDrawer from '@/components/calendar_mobile/drawer/MobileCalendarDrawer.vue';
import MobileCalendarDrawerHeader from '@/components/calendar_mobile/drawer/MobileCalendarDrawerHeader.vue';
import MobileCalendarDrawerBody from '@/components/calendar_mobile/drawer/MobileCalendarDrawerBody.vue';
import MobileSelectionHeader from '@/components/selection/MobileSelectionHeader.vue';
import MobileSelectionTabBar from '@/components/selection/MobileSelectionTabBar.vue';
import PopSelectionUsageMobile from '@/components/selection/PopSelectionUsageMobile.vue';
import ToggleDrawer from '@/components/calendar_mobile/ToggleDrawer.vue';

/** 상수 & Composables (Refactoring) */
import { CATEGORY_MAP, CATEGORY_ORDER, CATEGORY_NAME_TO_ID } from '@/constants/choiceCategories';
import { useChoiceSelection } from '@/composables/choice/useChoiceSelection';
import { useChoicePurchase } from '@/composables/choice/useChoicePurchase';
import { usePerpetualCalendarLogic } from '@/composables/calendar/usePerpetualCalendarLogic';
import { useChoiceCalendarLogic } from '@/composables/choice/useChoiceCalendarLogic';
import { useSelectionPopupState } from '@/composables/selection/useSelectionPopupState';
import { CHOICE_CATEGORIES } from '@/constants/choiceCategories'; // [Fix] Import here

/** 스타일 */
import '@/assets/nangman_style/calendar/calendar_web.scss';
import '@/assets/nangman_style/calendar/calendar_mobile.scss';

definePage({ meta: { layout: 'manse', title: '택일달력' } });

const router = useRouter();
const route = useRoute();
const store = useStore();

applySeoMeta({
  title: '역학달력, 음력, 절기, 간지, 사주시계',
  description:
    '매월 간지, 절기, 음력, 공휴일을 한눈에 보는 역학달력과 시·일·월·년 실시간 사주시계를 제공합니다. 로그인하면 맞춤택일까지 이용 가능합니다.',
  url: canonicalUrl(route.fullPath),
});
const userId = computed(() => store.getters['user/id']);

// =================== 반응형 breakpoint ===================
const { isMobile } = useDeviceDetect();
const isWebView = computed(() => typeof window !== 'undefined' && !!window.ReactNativeWebView);
// 하단 탭바가 네비게이션을 담당하므로 모바일(앱·웹)에서는 상단 헤더를 숨긴다
const showMobileHeader = computed(() => false);
const goBack = () => router.back();

const syncMobileLayoutChrome = () => {
  if (typeof document === 'undefined') return;
  const layoutEl = document.querySelector('.manse-layout');
  if (!layoutEl) return;
  layoutEl.classList.toggle('selection-mobile-mode', isMobile.value);
};

// [반응형] iOS Safari 스크롤 반동(bounce) 제거
const syncOverscrollBehavior = () => {
  if (typeof document === 'undefined') return;
  document.documentElement.style.overscrollBehavior = isMobile.value ? 'none' : '';
};

// [반응형] 모바일 전용 상태
const isMobileSidebarOpen = ref(false);
const selectedSidebarTab = ref('list');
const isDrawerOpen = ref(false); // ToggleDrawer (역학달력 옵션)
const isOpenPopSelectionUsage = ref(false); // 택일 가이드 팝업

const openMobileSidebar = () => {
  isMobileSidebarOpen.value = true;
};
const closeMobileSidebar = () => {
  isMobileSidebarOpen.value = false;
};

/** [반응형] 모바일 탭 변경 핸들러 */
const handleMobileTabChange = (tab) => {
  handleTabUpdate(tab);
};

// [추가] ChoiceStartPopup에서 선택 완료 시 호출될 핸들러
const bestDaySelection = ref(null); // [Best Day] 추천일 클릭 시 프리셋 데이터
const currentPurchaseItem = ref(null); // [추가] 현재 선택된 구매 아이템 (헤더 연동)

// [추가] 카테고리 매핑 (공통 사용)
// [추가] 카테고리 매핑 (공통 사용)
const categoryMap = CATEGORY_MAP;

/** [추가] 결제 완료된 날짜 정보 저장용 (택일 모드 UI 반영) */
const paidChoiceInfo = ref([]); // [New] 결제된 정보 목록 (Array of {startDate, endDate, mode})

// [Fix] 달력 셀 표시용 구간 데이터 캐시 - useSelectionPopupState에서 관리
// 구조: { guestUserId: { categoryId: { dateKey: { score, type, color, desc, energyLevel } } } }
const calendarRangeDataCache = ref({});

// [특수일] 손없는날/공망일/황도일 플래그 맵 { 'YYYY-MM-DD': { sonupneunday, gongmangil, hwangdoil } }
const specialCalendarMap = ref({});

// [특수일] base-service 응답의 특수일캘린더 배열을 specialCalendarMap에 병합
const mergeSpecialCalendar = (specialList) => {
  if (!Array.isArray(specialList) || specialList.length === 0) return;
  const newMap = { ...specialCalendarMap.value };
  specialList.forEach((item) => {
    const dateKey = item?.['날짜키'];
    if (!dateKey) return;
    newMap[dateKey] = {
      sonupneunday: item['손없는날'] === true,
      gongmangil: item['공망일'] === true,
      hwangdoil: item['황도일'] === true,
    };
  });
  specialCalendarMap.value = newMap;
};

// [Fix] 현재 카테고리 ID 추적 (useSelectionPopupState와 공유)
const currentCalendarCategoryId = ref(null);

// [Fix] 결제 완료 직후 플래그 (달력 로딩 스킵용)
const isPaymentJustCompleted = ref(false);

// [Fix] 현재 사주 + 카테고리의 달력 데이터 (useCalendar에 전달)
// 초기화 순서 문제 해결: useCalendar보다 먼저 선언
const currentCategoryCalendarData = computed(() => {
  const guestUserId = mySajuData.value?.guestUserId;
  const categoryId = currentCalendarCategoryId.value;
  const cacheData = calendarRangeDataCache.value;
  const userIdKey = String(guestUserId);
  const result = cacheData[userIdKey]?.[categoryId] || {};
  if (!guestUserId || !categoryId) return {};
  return result;
});

// [New] 현재 선택된 사주의 구매 내역만 필터링
const currentSajuPaidItems = computed(() => {
  if (!mySajuData.value || !mySajuData.value.guestUserId) return [];
  return paidChoiceInfo.value.filter((info) => info.guestUserId === mySajuData.value.guestUserId);
});

/**
 * 특정 카테고리의 구매 내역이 있는지 확인 (월간/주간/단일 모두 포함)
 * @param {string} categoryId - 카테고리 ID
 * @returns {boolean}
 */
const hasPurchaseForCategory = (categoryId, title = viewTitle.value) => {
  if (!currentSajuPaidItems.value?.length || !title) return false;
  const match = title.match(/(\d{4})년\s*(\d{1,2})월/);
  if (!match) return false;
  const y = match[1];
  const m = match[2].padStart(2, '0');
  const monthStart = `${y}${m}01`;
  const monthEnd = `${y}${m}31`;
  return currentSajuPaidItems.value.some(
    (item) =>
      item.category === categoryId && item.endDate >= monthStart && item.startDate <= monthEnd,
  );
};

/**
 * [Best Day] 현재 보는 달에 구매 이력이 있는지 확인
 */
const hasCurrentMonthPurchase = (title) => {
  if (!currentSajuPaidItems.value?.length || !title) return false;
  const match = title.match(/(\d{4})년\s*(\d{1,2})월/);
  if (!match) return false;
  const y = match[1];
  const m = match[2].padStart(2, '0');
  const monthStart = `${y}${m}01`;
  const monthEnd = `${y}${m}31`;
  return currentSajuPaidItems.value.some(
    (item) => item.endDate >= monthStart && item.startDate <= monthEnd,
  );
};

/**
 * [Best Day] 이달의 추천일 API 호출 + bestDayData 세팅
 * - 현재 월 기준 3개월 이내(현재월 ~ +2개월)만 표시
 * - 이전 달에서는 표시하지 않음
 */
const isBestDayLoading = ref(false);
const fetchBestDay = async () => {
  const guestUserId = mySajuData.value?.guestUserId;
  const title = viewTitle.value;
  if (!guestUserId || !title || currentTab.value !== 'choice') {
    bestDayData.value = null;
    return;
  }

  const match = title.match(/(\d{4})년\s*(\d{1,2})월/);
  if (!match) return;
  const year = parseInt(match[1]);
  const month = parseInt(match[2]);

  // [Best Day] bestDayData 노출 조건: 현재 월 기준 3개월 범위 + 당월 미구매
  // [특수일] 특수일캘린더는 위 조건과 무관하게 항상 수신 (API 응답에 포함됨)
  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth() + 1; // 1-indexed
  const viewDate = new Date(year, month - 1, 1);
  const minDate = new Date(currentYear, currentMonth - 1, 1);
  const maxDate = new Date(currentYear, currentMonth + 2, 0);
  const inRange = viewDate >= minDate && viewDate <= maxDate;
  const canShowBestDay = inRange && !hasCurrentMonthPurchase(title);

  isBestDayLoading.value = true;
  try {
    const result = await getBestDayAPI({ guestUserId, year, month });
    const rawData = result?.data || result;

    // [특수일] 특수일캘린더에서 손없는날/공망일/황도일 플래그 추출 (bestDay 조건과 무관하게 항상 수신)
    mergeSpecialCalendar(rawData?.특수일캘린더);

    const best = rawData?.이달의베스트;
    if (canShowBestDay && best && best.날짜키) {
      const categoryId = CATEGORY_NAME_TO_ID[best.활동] || 'contract';
      bestDayData.value = {
        date: best.날짜키,
        category: categoryId,
        reportType: 'day',
        isLight: false,
        price: 0,
        score: best.확률,
        summary: best.한줄요약 || '',
      };
    } else {
      bestDayData.value = null;
    }
  } catch (e) {
    console.error('[BestDay] API 호출 실패:', e);
    bestDayData.value = null;
  } finally {
    isBestDayLoading.value = false;
  }

  // bestDayData 변경 후 FullCalendar re-render (eventContent 콜백 재실행)
  nextTick(() => {
    calendarApi.value?.render();
  });
};

// [추가] 결제 팝업 상태 및 데이터
// [Refactoring] choiceStates (isChoicePaymentOpen, choicePaymentInfo, choicePaymentPayload, choiceTicketsData, choiceTicketCount) moved to useSelectionPopupState

// [추가] 결제 팝업 닫기
// [Removed] closeChoicePaymentPopup moved to handleCloseChoicePaymentPopup

// [New] 중복 구매 체크 함수
// [New] 중복 구매 체크 함수
// [Refactoring] isDuplicatePurchase, isSameDate removed (imported from composable)

// [New] 결제 타입별 팝업 열기 헬퍼 함수
const openPopupByReportType = async (reportType, payload, info) => {
  const categoryId = payload.category;
  const categoryName = payload.name || info.name;

  if (reportType === 'day' || reportType === 'single') {
    popupInitialView.value = 'day';
    openDailyPopup(new Date(payload.startDate), categoryId || categoryName);
  } else {
    setPopupContext(new Date(payload.startDate), categoryId || categoryName);
    popupInitialView.value = reportType; // 'week' or 'month'
    isDailyPopupOpen.value = true;
  }
  await nextTick();
};

// [추가] 결제 성공 핸들러
const handleChoicePaymentSuccess = async () => {
  isChoicePaymentOpen.value = false;
  isChoicePopupOpen.value = false; // [New] 결제 완료 시 시작 팝업도 닫기

  // [Fix] 이전 drawer(ChoiceStartPopup)가 완전히 닫힌 후 결과 drawer를 열도록 대기
  // Vuetify drawer 전환 애니메이션(~300ms) 중 스크롤 전략이 충돌하여 휠 스크롤이 안 되는 문제 방지
  await new Promise((resolve) => setTimeout(resolve, 350));

  // [Fix] 결제 성공 시 해당 월 캐시 무효화 (새 데이터 fetch를 위해)
  if (choicePaymentPayload.value?.startDate && choicePaymentPayload.value?.category) {
    const startDate = new Date(choicePaymentPayload.value.startDate);
    const yearMonth = `${startDate.getFullYear()}년 ${startDate.getMonth() + 1}월`;
    invalidateCalendarCache(
      mySajuData.value?.guestUserId,
      choicePaymentPayload.value.category,
      yearMonth,
    );
  }

  // [Fix] 카테고리를 paidChoiceInfo 추가 전에 먼저 설정 (purchaseList watcher보다 먼저 실행되도록)
  if (choicePaymentInfo.value && choicePaymentInfo.value.name) {
    const itemName = choicePaymentInfo.value.name;
    // [Fix] 결제 완료 직후 플래그 설정 (달력 로딩보다 팝업이 먼저 열리도록)
    isPaymentJustCompleted.value = true;
    currentPurchaseItem.value = itemName;
  }

  // [추가] 결제 성공 시 정보를 paidChoiceInfo에 저장 (UI 반영용)
  if (choicePaymentPayload.value && choicePaymentPayload.value.startDate) {
    // 1. 원본 (주간/월간) 내역 저장
    const newItem = createPaidItem(
      choicePaymentPayload.value,
      choicePaymentInfo.value,
      mySajuData.value?.guestUserId,
    );
    paidChoiceInfo.value.push(newItem);

    // 2. [Refactoring] 주간(week) 또는 월간(month) 구매 시 개별 일자 아이템 추가
    const additionalItems = generateDayItems(
      choicePaymentPayload.value,
      choicePaymentInfo.value,
      paidChoiceInfo.value,
      mySajuData.value?.guestUserId,
    );

    if (additionalItems.length > 0) {
      paidChoiceInfo.value.push(...additionalItems);
    }
  }

  bestDaySelection.value = null;
  setSelectionMode('single');

  const api = refCalendar.value?.getApi();
  if (api) {
    const targetDate = choicePaymentPayload.value?.startDate;

    api.unselect();
    api.render(); // [중요] 상태 업데이트 후 렌더링

    // [New] 렌더링 완료 후 달력 이동 보장 (setTimeout 사용)
    if (targetDate) {
      setTimeout(() => {
        navigateCalendar({ type: 'goto', date: targetDate });
      }, 100);
    }
  }
  selectedDate.value = null;
  selectedRange.value = null;

  // [Fix] 결제 성공 후 달력 데이터 명시적 새로고침 (같은 달에서 결제 시 viewTitle watch가 트리거되지 않으므로)
  if (choicePaymentPayload.value?.category && viewTitle.value && mySajuData.value?.guestUserId) {
    fetchRangeDataForMonth(
      viewTitle.value,
      mySajuData.value.guestUserId,
      choicePaymentPayload.value.category,
    );
  }

  // 성공 토스트 또는 스낵바
  triggerSnackbar('택일 조회가 완료되었습니다.');

  // [Fix] 결제 타입별 팝업 열기 (팝업 먼저 열고 → 데이터 로딩)
  if (choicePaymentPayload.value?.reportType) {
    const payloadReportType = choicePaymentPayload.value.reportType;
    const payloadStartDate = formatDate(new Date(choicePaymentPayload.value.startDate), 'YYYYMMDD');
    const payloadCategory = choicePaymentPayload.value.category;

    // [Mod] 단일(13) + 구간(14) 분리 요청: rangeInfo 전달
    let rangeInfo = null;

    if (payloadReportType === 'week' || payloadReportType === 'month') {
      // [Fix] 주간/월간 구매 시: 기존에 더 큰 구간(예: 30일 라이트)이 있으면 해당 rangeInfo 사용
      // (7일 집중 구매 후 팝업 탭이 light30으로 결정되는데, 데이터는 7일만 로드되는 문제 방지)
      const existingRange = findMatchingRangeReport(
        currentSajuPaidItems.value,
        payloadStartDate,
        payloadCategory,
      );
      if (existingRange && existingRange.reportType === 'month' && payloadReportType === 'week') {
        // 기존 30일 구간이 있으면 30일 데이터로 요청
        rangeInfo = existingRange;
      } else {
        rangeInfo = {
          reportType: payloadReportType,
          startDate: payloadStartDate,
        };
      }
    } else {
      // [Fix] 1일 집중 구매 시: 기존에 구매된 30일/7일 리포트가 있으면 해당 rangeInfo 사용
      // (탭 전환 시 30일 라이트 등의 데이터도 함께 로드되도록)
      rangeInfo = findMatchingRangeReport(
        currentSajuPaidItems.value,
        payloadStartDate,
        payloadCategory,
      );
    }

    // 팝업 열기 (await로 DOM 렌더링 대기)
    await openPopupByReportType(
      payloadReportType,
      choicePaymentPayload.value,
      choicePaymentInfo.value,
    );

    // [Fix] 팝업 데이터는 팝업이 열린 후 로딩 (팝업 내부에서 로딩 스피너 표시)
    await fetchFocus1Detail(
      mySajuData.value?.guestUserId,
      choicePaymentPayload.value.startDate,
      payloadCategory,
      rangeInfo,
    );
  }

  // [Fix] 결제 성공 후 크레딧 바(포인트/티켓) 즉시 갱신
  if (paymentsStore) {
    await paymentsStore.getOwnPoint();
    await paymentsStore.fetchTicketCount();
  }
};

const handleReqCharge = (shortageAmount) => {
  router.push({
    path: '/purchase/points',
    query: { next: route.fullPath, shortageAmount: shortageAmount || 0 },
  });
};

const handleSelectionComplete = async (selectionData) => {
  const categoryName = categoryMap[selectionData.category] || selectionData.category;
  // [Mod] 결제하기 클릭 시에는 카테고리 변경하지 않음 (결제 성공 후에만 변경)
  // currentPurchaseItem.value = categoryName;

  // [Mod] 결제 전이므로 달력 하이라이트/모드 변경하지 않음
  // 결제 성공 후 handleChoicePaymentSuccess에서 처리

  let start = null;
  let end = null;

  // 날짜 계산만 수행 (UI 변경 없음)
  if (selectionData.reportType === 'month') {
    const baseDate = selectionData.date ? new Date(selectionData.date) : new Date();
    start = new Date(baseDate.getFullYear(), baseDate.getMonth(), 1);
    end = new Date(baseDate.getFullYear(), baseDate.getMonth() + 1, 0);
  } else if (selectionData.reportType === 'week') {
    if (selectionData.date && selectionData.date.start) {
      start = new Date(selectionData.date.start);
      end = new Date(selectionData.date.end);
    }
  } else {
    start = new Date(selectionData.date);
    end = new Date(start);
  }

  // [Refactoring] formatDateRangeStr 사용
  const dateRangeStr = formatDateRangeStr(selectionData.reportType, start, end);

  // [Check] 중복 구매 체크 (Deleted: ChoiceStartPopup에서 처리함)

  // [Refactoring] generateItemName 사용
  const itemName = generateItemName(selectionData.isLight, selectionData.reportType);

  choicePaymentInfo.value = {
    name: categoryName,
    pointPrice: selectionData.price ?? 300,
    itemName: itemName, // [New] 동적 상품명 추가
  };

  choicePaymentPayload.value = {
    ...selectionData,
    startDate: start,
    endDate: end,
    dateRangeStr,
    guestUserId: mySajuData.value?.guestUserId,
  };

  // [Best Day] 무료 추천일은 확인/결제 팝업 없이 바로 API 호출
  if (bestDaySelection.value) {
    isLoading.value = true;
    try {
      const targetDate = new Date(start);
      const params = {
        guestUserId: Number(mySajuData.value?.guestUserId),
        analysisTypeId: selectionData.isLight ? 14 : 13,
        paymentMethod: 'best',
        category: selectionData.category,
        year: targetDate.getFullYear(),
        month: targetDate.getMonth() + 1,
        day: targetDate.getDate(),
        dayRange: 1,
      };
      const res = await postChoiceLogAPI(params);
      if (res) {
        handleChoicePaymentSuccess();
      }
    } catch (error) {
      console.error('[BestDay] 결제 실패:', error);
      showAlert({ title: '결제에 실패했습니다. 다시 시도해주세요.' });
    } finally {
      isLoading.value = false;
    }
    return;
  }

  choiceConfirmData.value = {
    category: categoryName,
    itemName: itemName,
    dateText: dateRangeStr,
  };
  isChoiceConfirmOpen.value = true;
};

// [New] 커스텀 확인 팝업 상태 및 핸들러
// [Refactoring] isChoiceConfirmOpen, choiceConfirmData moved to useSelectionPopupState

// [New] Fetch and Set Ticket Info from Store (No API Call)
const setTicketInfoFromStore = (isLight, reportType) => {
  isTicketLoading.value = true;
  try {
    const details = paymentsStore.choiceTicketDetails;
    const typeKey = isLight ? 'light' : 'concentrated';
    let periodKey = null;

    if (reportType === 'week') periodKey = 'day7';
    else if (reportType === 'month') periodKey = 'day30';

    // day(1일)은 리포트권 사용 불가 (임시) or day7 사용?
    // 기획상 1일권은 없고 7일/30일권만 있음. 1일 리포트는 건별 포인트 소진.
    // 하지만 만약 1일 리포트 선택 시 티켓 사용 불가라면 count 0.

    if (periodKey && details && details[typeKey]) {
      const ticketInfo = details[typeKey][periodKey];
      const count = ticketInfo?.count || 0;
      choiceTicketCount.value = count;
      choiceTicketsData.value = {
        name: `${isLight ? '라이트' : '집중'} ${periodKey === 'day7' ? '7일' : '30일'} 이용권`,
        count: count,
      };
    } else {
      choiceTicketCount.value = 0;
      choiceTicketsData.value = null;
    }
  } catch (e) {
    console.error('Failed to set ticket info:', e);
    choiceTicketCount.value = 0;
    choiceTicketsData.value = null;
  } finally {
    isTicketLoading.value = false;
  }
};

const onConfirmChoice = async () => {
  isChoiceConfirmOpen.value = false;
  // [New] Fetch Ticket Info
  const reportType = choicePaymentPayload.value?.reportType;
  const isLight = choicePaymentPayload.value?.isLight;
  // await fetchAndSetTicketInfo(isLight, reportType); // Removed
  setTicketInfoFromStore(isLight, reportType);
  isChoicePaymentOpen.value = true;
};

// [Refactoring] closeChoicePaymentPopup and onCloseChoiceConfirm logic moved to useSelectionPopupState or integrated below
const handleCloseChoicePaymentPopup = () => {
  isChoicePaymentOpen.value = false;
  setSelectionMode('single'); // [Fix] 선택 모드 초기화
  const api = refCalendar.value?.getApi();
  if (api) api.unselect();
  selectedDate.value = null;
  selectedRange.value = null;
  isChoicePopupOpen.value = true;
};

const handleCloseChoiceConfirm = () => {
  isChoiceConfirmOpen.value = false;
  setSelectionMode('single'); // [Fix] 선택 모드 초기화
  const api = refCalendar.value?.getApi();
  if (api) api.unselect();
  selectedDate.value = null;
  selectedRange.value = null;
};

// [New] 현재 선택된 구매 아이템에 해당하는 결제 정보만 필터링
const filteredPaidChoiceInfo = computed(() => {
  if (!currentPurchaseItem.value) return [];
  return currentSajuPaidItems.value.filter((info) => info.name === currentPurchaseItem.value);
});

const paymentsStore = usePaymentsStore();
const primarySajuStore = usePrimarySajuStore();

/** 로딩 인디케이터 상태 */
const isLoading = ref(false);
const errorMessage = ref('');

// [New] 달력용 구간 데이터 로딩 상태 (useSelectionPopupState에서 가져옴)
// isCalendarRangeLoading은 composable에서 반환됨 - 아래에서 destructuring

// 1. [수정] 괄호() 추가하여 함수 실행
const { getBirthTimeTxt } = fiveMixin();

const {
  formatNumber,
  getGenderTxt,
  getDayOfWeekOne,
  formatOption1Txt,
  formatOption2Txt,
  formatOption4Txt,
} = useFunctions();

// 팝업 관리자
const {
  dialogTitle,
  dialogTitleClass, // [추가]
  dialogTitle2,
  dialogTitle2Class,
  dialogMessage, // [추가]
  dialogText,
  dialogNextUrl,
  alertRef,
  confirmRef,
  showAlert,
  showConfirm,
  dialogCancelText, // [추가]
  dialogCancel, // [추가]
} = useDialogManager();

// [Refactoring] Use Choice Composables
const { isDuplicatePurchase, isSameDate, generateItemName, formatDateRangeStr } =
  useChoiceSelection();

const { createPaidItem, generateDayItems } = useChoicePurchase();

/** 사주시계 관리 */
const { sajuClockRef, isSajuClockOpen, setSajuClockOpen } = useSajuClock();

const selectionHeaderRef = ref(null);

// [Refactoring] useSelectionPopupState moved below useCalendar to avoid ReferenceError on eventByDate

/** 달력 필터 */
const calendarFilter = useCalendarFilter({ isKorCheckedDefault: true });

/** 스낵바 */
const { snackbarRef, triggerSnackbar } = useSnackbar();

// ============================================================
// ⚠️ [핵심] 순서 재배치: 데이터 로드 -> 단순 팝업 -> Validator -> 복합 팝업 -> Validation Hook
// ============================================================

/** (Step 1) 내 사주 데이터 로드 (가장 먼저) */
const {
  mySajuData,
  mySajuList,
  isMySajuDataExist,
  isMySajuLoading,
  isMySajuLoaded,
  fetchMySajuList,
  selectMySaju,
} = useMySaju({
  userId,
  errorMessage,
});

/** (Step 2) 파싱 정보 생성 */
const parsedMySajuInfo = useParsedMySajuInfo({
  mySajuData,
  mySajuList,
  isMySajuDataExist,
  utils: {
    formatDate,
    getGenderTxt,
    getDayOfWeekOne,
    formatOption1Txt,
    formatOption2Txt,
    formatOption4Txt,
    getBirthTimeTxt,
  },
});

/** (Step 3) 의존성 없는 단순 팝업 함수들 정의 */
const showLoginConfirm = () => {
  showConfirm({
    title: '로그인 하시면',
    title2: '택일을 보실 수 있습니다.',
    text: '로그인 하러 가기',
    onConfirm: () =>
      router.push({
        path: '/auth/login',
        query: { to: route.fullPath },
      }),
  });
};

const showUnknownTimeConfirm = () => {
  showAlert({
    title: `시간 모름인 사주는\n일진서비스 제공이 어렵습니다.`,
    onConfirm: () => {},
  });
};

/** [임시] 구매 목록 테스트 데이터 관리 */
// const purchaseList = ref([]); // [Mod] computed로 변경
const toggleTestData = () => {
  if (purchaseList.value.length > 0) {
    // purchaseList.value = []; // [Mod] Cannot set computed
    // currentPurchaseItem.value = null;
    // 테스트 데이터를 끄려면 paidChoiceInfo를 비워야 함 (혹은 테스트용 데이터만 제거)
    // 여기서는 간단히 새로고침 권장 로직으로 두거나, paidChoiceInfo 초기화
    paidChoiceInfo.value = [];
    currentPurchaseItem.value = null;
  } else {
    // 테스트 데이터 추가
    const categories = [
      '계약/협약',
      '이사/입주',
      '병원/검진',
      '연애/고백',
      '서류/등록',
      '면접/오디션',
      '시험/자격증',
      '금전/지출',
      '여행/출장',
      '개업/오픈',
    ];
    // paidChoiceInfo에 더미 데이터 추가 (현재 사주 기준)
    categories.forEach((cat) => {
      paidChoiceInfo.value.push({
        name: cat,
        category: 'test',
        reportType: 'day',
        startDate: '20240101',
        endDate: '20240101',
        mode: 'day',
        guestUserId: mySajuData.value?.guestUserId,
      });
    });
  }
};

const showFutureSajuConfirm = () => {
  showAlert({
    title: `내년 이후 사주는\n일진서비스 제공이 어렵습니다.`,
    onConfirm: () => {},
  });
};

/** 내 사주 등록 알림창 표시 */
const showSelectConfirm = () => {
  let filteredSajuList = [];
  let title = '등록 사주 저장 후,';
  let title2 = '택일을 보실 수 있습니다.';
  let text = '등록 사주 저장';

  if (mySajuList.value && mySajuList.value.length > 0) {
    filteredSajuList = mySajuList.value.filter((saju) => {
      return !isUnknownBirthTime(saju.guestUserId) && !isFutureSaju(saju.guestUserId);
    });
  }

  if (filteredSajuList && filteredSajuList.length > 0) {
    title = '등록 사주를 선택하여,';
    title2 = '택일을 확인하세요.';
    text = '확인';
  }

  let onConfirm = () => {
    if (!mySajuList.value || filteredSajuList.length === 0) {
      router.push('/saju-input');
    } else {
      // 사주가 존재하는 경우에 할 행동 (예: 팝업 닫기 또는 다음 단계 진행)
    }
  };

  showConfirm({ title, title2, text, onConfirm });
};

// [추가] 사주 미등록 시 택일 진입 차단 팝업
const showRegisterSajuConfirm = () => {
  showConfirm({
    title: '등록 사주 저장 후,',
    title2: '택일을 보실 수 있습니다.',
    text: '등록 사주 저장',
    onConfirm: () => {
      router.push('/saju-input');
    },
  });
};

// [추가] 3개월 범위 벗어남 경고 팝업
const handleDateRangeAlert = (type = 'future') => {
  const { title, text } = getChoiceRangeErrorMessage(type);
  showAlert({ title, text });
};

/** '내 사주' 선택 유효성 검증 로직 */
const { isUnknownBirthTime, isFutureSaju, validateMySaju } = useMySajuValidator({
  mySajuList,
  onUnknownTime: () => showUnknownTimeConfirm(),
  onFutureSaju: () => showFutureSajuConfirm(),
});

// ============================================================

// ============================================================
// 📅 [추가] 택일 모드 로직 (1일 / 7일 / 월간)
// ============================================================

// 1. 선택 모드 상태 (기본값: 'single')
const selectionMode = ref('single');
// [추가] 현재 탭 상태 (기본값: 'perpetual'), URL 쿼리 파라미터 우선
const currentTab = ref(route.query.tab || 'perpetual');

// [추가] 탭 변경 핸들러
const isInternalTabChange = ref(false); // 내부 탭 클릭 여부 확인

const handleTabUpdate = (tabValue) => {
  if (tabValue === currentTab.value) return;
  isInternalTabChange.value = true; // 내부 클릭임을 표시
  currentTab.value = tabValue;

  updateSelectionModeByTab(tabValue);

  // [추가] 탭 변경 시 URL 쿼리 파라미터 업데이트 (로그인 후 복귀 등 위해 필요)
  router.replace({ query: { ...route.query, tab: tabValue } });
};

// 탭에 따른 모드/상태 업데이트 로직 분리
const updateSelectionModeByTab = (tabValue) => {
  // 택일(choice) 탭이 아니면 단일 선택 모드로 초기화
  if (tabValue !== 'choice') {
    setSelectionMode('single');

    // [수정] 역학달력 탭으로 돌아오면:
    // 1. 음력/절기/공휴일은 무조건 켠다 (달력 기본 기능)
    calendarFilter.isLunarChecked.value = true;
    calendarFilter.isSeasonChecked.value = true;
    calendarFilter.isHolidayChecked.value = true;

    // 2. 한글/컬러 표시는 '기존 설정 유지'를 위해 강제로 켜지 않음.
    // (단, GNB로 진입하거나 새로고침 시에는 onMounted/watch에서 켜줄 예정)
    // calendarFilter.isKorChecked.value = true;
    // calendarFilter.isColorChecked.value = true;
  } else {
    // [추가] 택일 탭으로 전환 시 기존 선택(하이라이트) 제거
    const api = refCalendar.value?.getApi();
    if (api) api.unselect();
    selectedDate.value = null;
    selectedRange.value = null;

    // [추가] 택일 탭 초기 모드 설정 (충돌 방지를 위해 day 사용)
    setSelectionMode('day');

    // [추가] 택일 탭에서는 음력/절기/공휴일 끄기 (한글/컬러는 역학달력 설정 유지)
    calendarFilter.isLunarChecked.value = false;
    calendarFilter.isSeasonChecked.value = false;
    calendarFilter.isHolidayChecked.value = false;

    // [Fix] 택일 탭 전환 시 달력 데이터 로드는 아래 watch(currentTab)에서 처리
    // (fetchRangeDataForMonth가 이 시점에 아직 정의되지 않음)
  }
};

// ... (refs declaration)

// [추가] URL 쿼리(GNB 등 외부 진입) 변경 감지
watch(
  () => route.query.tab,
  (newTab) => {
    // 내부 탭 클릭으로 인한 변경이면 무시 (이미 handleTabUpdate에서 처리됨)
    if (isInternalTabChange.value) {
      isInternalTabChange.value = false; // 플래그 초기화
      return;
    }

    // GNB나 뒤로가기 등으로 인한 외부 변경인 경우
    const targetTab = newTab || 'choice'; // 기본값 choice
    if (currentTab.value !== targetTab) {
      currentTab.value = targetTab;

      // 외부 진입 시 '역학달력'이면 모든 옵션 초기화(ON)
      if (targetTab === 'perpetual' || targetTab !== 'choice') {
        calendarFilter.isKorChecked.value = true;
        calendarFilter.isColorChecked.value = true;
        // 나머지는 updateSelectionModeByTab에서 켜짐
      }

      updateSelectionModeByTab(targetTab);
    } else if (targetTab === 'perpetual') {
      // 이미 탭은 같지만 GNB 클릭(새로고침 성격)일 수 있음 -> 강제 옵션 ON
      // (URL이 동일하면 watch가 안 돌 수 있으나 query가 바뀌지 않으면 여기 안 옴.
      // 만약 ?tab=perpetual 상태에서 GNB(?tab=perpetual) 누르면 라우터가 반응 안 할 수 있음.
      // 하지만 다른 페이지 갔다 왔으면 반응함.)
      // 이 블록은 '탭이 변경되었을 때' 실행됨. 같은 탭이면?
      // 같은 URL push는 watch trigger 안 됨.
      // 하지만 이 요구사항은 "GNB로 들어오면" -> 보통 다른 페이지에서 옴.
    }
  },
);

// [추가] 선택된 날짜 범위 저장 (시작일, 종료일)
const selectedRange = ref(null);

// [Refactoring] isDailyPopupOpen, isChoicePopupOpen, openChoicePopup moved to useSelectionPopupState

// 2. 모드 변경 함수
const setSelectionMode = (mode) => {
  // [수정] 택일 탭에서 'single'로 설정 들어오면 'day'로 변환
  if (currentTab.value === 'choice' && mode === 'single') {
    mode = 'day';
  }

  selectionMode.value = mode;

  const api = refCalendar.value?.getApi();
  if (!api) return;

  // 모드 변경 시 기존 선택 해제
  api.unselect();
  // [추가] 기존 단일 선택(useCalendar 내부 상태)도 해제하여 중복 하이라이트 방지
  selectedDate.value = null;
  // [추가] 범위 초기화
  selectedRange.value = null;

  // 월간 모드일 경우 즉시 현재 달 전체 선택
  if (mode === 'month') {
    const currentDate = api.getDate(); // 현재 보고 있는 날짜 (보통 월의 1일)
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const monthStart = new Date(year, month, 1);
    const monthEnd = new Date(year, month + 1, 1); // 다음 달 1일 (FullCalendar는 end가 exclusive)

    api.select(monthStart, monthEnd);
    // [추가] 범위 저장 (표시용은 inclusive - 말일)
    const rangeEnd = new Date(year, month + 1, 0);
    selectedRange.value = { start: monthStart, end: rangeEnd };
  } else if (mode === 'day') {
    // 택일 단일 모드 전환 시 별도 초기화 동작 없음
  }
};

/** 달력 생성 */
const {
  calendarApi,
  calendarOptions: _calendarOptions, // 이름 변경하여 받아옴
  refCalendar,
  viewTitle,
  isToday,
  firstSeason,
  firstSeasonMonth,
  navigateCalendar,
  getDailyFortuneCalendar,
  selectedDate, // [추가]
  setSelectedDate, // [추가]
  eventByDate, // [추가]
  bestDayData, // [Best Day] 이달의 추천일
} = useCalendar({
  calendarType: computed(() => (isMobile.value ? 'mobile' : 'web')),
  isLoading,
  isLunarChecked: calendarFilter.isLunarChecked,
  isColorChecked: calendarFilter.isColorChecked,
  isSeasonChecked: calendarFilter.isSeasonChecked,
  isKorChecked: calendarFilter.isKorChecked,
  isHolidayChecked: calendarFilter.isHolidayChecked,
  userId,

  isSajuDataExist: isMySajuDataExist,
  selectionMode, // [추가]
  isChoiceMode: computed(() => currentTab.value === 'choice'), // [추가] 택일 모드 여부 전달 for 글자색 변경
  paidChoiceInfo: filteredPaidChoiceInfo, // [추가] 결제된 날짜/범위 정보 전달 (필터링 적용)
  calendarRangeDataCache: currentCategoryCalendarData, // [FIX] 현재 카테고리 데이터만 전달
  specialCalendarMap, // [특수일] 손없는날/공망일/황도일 맵 전달
});

// ===== 최적화: viewTitle 파싱 결과 캐싱 =====
const viewDateRange = computed(() => {
  const title = viewTitle.value;
  if (!title) return null;

  const match = title.match(/^(\d{4})년\s?(\d{1,2})월/);
  if (!match) return null;

  const year = parseInt(match[1], 10);
  const month = parseInt(match[2], 10) - 1; // 0-indexed
  return {
    start: new Date(year, month, 1),
    end: new Date(year, month + 1, 0, 23, 59, 59),
  };
});

// ===== [구독] 택일 구독 조회 (구독중 + 내사주 + canAccess 월 → 집중×10카테고리×30일 무료 조회) =====
const { hasChoiceAccess, fetchChoiceAccess } = useChoiceSubscriptionAccess();

/** 현재 보고 있는 달 → { year, month(1-indexed) } (viewTitle "YYYY년 M월" 파싱) */
const viewYearMonth = computed(() => {
  const m = viewTitle.value?.match(/^(\d{4})년\s?(\d{1,2})월/);
  if (!m) return null;
  return { year: parseInt(m[1], 10), month: parseInt(m[2], 10) };
});

/**
 * 구독으로 이 달의 택일을 무료 조회할 수 있는 상태인지.
 * 조건: 구독중 && 선택 사주 = 내사주 && 해당 월 canAccess(집중 13)
 */
const isChoiceSubscriptionCovered = computed(() => {
  const gid = mySajuData.value?.guestUserId;
  const ym = viewYearMonth.value;
  if (!gid || !ym) return false;
  return (
    paymentsStore.isSubscribed &&
    // guestUserId가 문자열로 들어올 수 있어 Number로 비교 (isMySaju는 strict ===)
    primarySajuStore.isMySaju(Number(gid)) &&
    hasChoiceAccess(gid, ym.year, ym.month)
  );
});

// 구독중 + 내사주일 때, 보는 달이 바뀌면 해당 월 canAccess 조회 (캐시 채움 → computed 갱신)
watch(
  [() => mySajuData.value?.guestUserId, viewYearMonth, () => paymentsStore.isSubscribed],
  ([gid, ym, subscribed]) => {
    if (!subscribed || !gid || !ym) return;
    if (!primarySajuStore.isMySaju(Number(gid))) return;
    fetchChoiceAccess(gid, ym.year, ym.month);
  },
  { immediate: true },
);

/**
 * [구독] 커버 월 카테고리 선택 시, 달력 렌더용 가상 paidChoiceInfo(집중·월간) 합성.
 * - 구매 경로(handleChoicePaymentSuccess)와 동일하게 createPaidItem + generateDayItems(일자항목)를 push해야
 *   달력 도장/구간이 렌더된다. (실제 구매/이력은 생성하지 않음 — source:'subscription' 표식)
 * - 같은 (사주, 카테고리, 달)의 항목이 이미 있으면 스킵.
 */
const ensureSubscriptionPaidItem = (catId) => {
  const gid = mySajuData.value?.guestUserId;
  const ym = viewYearMonth.value;
  if (!gid || !ym || !catId) return;

  const startDate = new Date(ym.year, ym.month - 1, 1);
  const endDate = new Date(ym.year, ym.month, 0); // 해당 달 말일
  const startStr = formatDate(startDate, 'YYYYMMDD');

  const exists = paidChoiceInfo.value.some(
    (it) =>
      it.guestUserId === gid &&
      it.category === catId &&
      it.reportType === 'month' &&
      it.startDate === startStr,
  );
  if (exists) return;

  const payload = {
    category: catId,
    reportType: 'month',
    startDate,
    endDate,
    isLight: false,
    analysisTypeId: 13,
  };
  const info = { name: CATEGORY_MAP[catId] };

  const main = { ...createPaidItem(payload, info, gid), source: 'subscription' };
  const days = generateDayItems(payload, info, paidChoiceInfo.value, gid).map((d) => ({
    ...d,
    source: 'subscription',
  }));
  paidChoiceInfo.value.push(main, ...days);
};

// Helper: YYYYMMDD 또는 Date 문자열 파싱
const parseDateString = (str) => {
  if (!str) return null;
  if (str instanceof Date) return str;

  let date = new Date(str);
  // YYYYMMDD 포맷 처리
  if (isNaN(date.getTime()) && typeof str === 'string' && str.length === 8) {
    date = new Date(`${str.slice(0, 4)}-${str.slice(4, 6)}-${str.slice(6, 8)}`);
  }
  return isNaN(date.getTime()) ? null : date;
};

// [Optimized] purchaseList (viewDateRange 활용으로 중복 파싱 제거)
const purchaseList = computed(() => {
  // [구독] 커버 월: 구매내역 대신 카테고리 10개 전체를 조회용으로 노출
  if (isChoiceSubscriptionCovered.value) {
    return [...CATEGORY_ORDER];
  }

  const unique = new Set();
  const range = viewDateRange.value;

  for (const item of currentSajuPaidItems.value) {
    if (!item.name) continue;

    // 날짜 범위 체크
    if (range && item.startDate) {
      const itemStart = parseDateString(item.startDate);
      const itemEnd = parseDateString(item.endDate) || itemStart;

      if (itemStart && itemEnd) {
        // 교차 조건: (ItemStart <= ViewEnd) AND (ItemEnd >= ViewStart)
        if (itemStart <= range.end && itemEnd >= range.start) {
          unique.add(item.name);
        }
        continue;
      }
    }

    // 날짜 정보 없으면 포함
    unique.add(item.name);
  }

  return Array.from(unique).sort((a, b) => CATEGORY_ORDER.indexOf(a) - CATEGORY_ORDER.indexOf(b));
});

// [반응형] MobileSelectionHeader에서 사용하는 형태 (객체 배열)로 변환
const uniquePaidItems = computed(() => {
  return purchaseList.value.map((name) => ({ name }));
});

/** [반응형] 모바일 헤더 이벤트 핸들러 */
const handleSelectPurchaseItem = (itemName) => {
  currentPurchaseItem.value = itemName;
  // 날짜 선택 초기화
  selectedDate.value = null;
  selectedRange.value = null;
};

/** [반응형] 모바일 헤더의 택일 시작하기 클릭 가드 (PC와 동일한 조건) */
// [구독] 커버 시: 시작 팝업(구매 플로우) 대신 카테고리 드롭다운 열고 안내 토스트
const onChoiceStartCovered = () => {
  selectionHeaderRef.value?.openDropdown?.();
  triggerSnackbar('구독 회원은 카테고리를 선택해 바로 조회할 수 있어요.');
};

// PC 헤더 '택일 시작하기' 클릭
const onReqStartSelection = () => {
  if (isChoiceSubscriptionCovered.value) {
    onChoiceStartCovered();
    return;
  }
  openChoicePopup();
};

const handleMobileOpenChoicePopup = () => {
  if (!userId.value) {
    showLoginConfirm();
    return;
  }
  if (!isMySajuDataExist.value) {
    showRegisterSajuConfirm();
    return;
  }
  if (isChoiceSubscriptionCovered.value) {
    onChoiceStartCovered();
    return;
  }
  openChoicePopup();
};

const openMySajuDrawerMobile = () => {
  // 드롭다운 닫기
  selectionHeaderRef.value?.closeDropdown?.();
  isMobileSidebarOpen.value = true;
  selectedSidebarTab.value = 'list';
};

// [New] Watcher: purchaseList 변경 시 currentPurchaseItem 유효성 검사 및 자동 선택
watch(purchaseList, (newList, oldList) => {
  // 배열 내용이 실제로 바뀌었는지 체크 (Shallow comparison for simplicity, as it's strings)
  const isExactlySame =
    newList.length === oldList?.length && newList.every((val, index) => val === oldList[index]);
  if (isExactlySame) return;

  // 1. 현재 선택된 아이템이 목록에 없으면 해제
  if (currentPurchaseItem.value && !newList.includes(currentPurchaseItem.value)) {
    currentPurchaseItem.value = null;
  }

  // 2. [New] 선택된 아이템이 없고 목록이 존재하면 첫 번째 아이템 자동 선택
  //    [구독] 커버 월에서는 자동선택 금지 → 드롭다운을 열어 사용자가 직접 카테고리를 고르게 함
  if (!currentPurchaseItem.value && newList.length > 0) {
    if (isChoiceSubscriptionCovered.value) {
      nextTick(() => selectionHeaderRef.value?.openDropdown?.());
      return;
    }
    currentPurchaseItem.value = newList[0];
  }
});

// [Optimized] 카테고리 변경 통합 watcher (선택 초기화 + 히스토리 조회 + 달력 데이터)
watch(currentPurchaseItem, async (newName) => {
  // 1. 선택 상태 초기화 (배경색 잔상 방지)
  const api = refCalendar.value?.getApi();
  if (api) api.unselect();
  selectedDate.value = null;
  selectedRange.value = null;

  // 2. 카테고리별 히스토리 조회 + 달력용 구간 API 호출
  if (newName) {
    const catId = CHOICE_CATEGORIES.find((c) => c.name === newName)?.id;
    if (catId) {
      await fetchAnalysisHistory(catId);

      // [Fix] 결제 완료 직후에는 달력 로딩 스킵 (팝업이 먼저 열리도록)
      if (isPaymentJustCompleted.value) {
        isPaymentJustCompleted.value = false;
        // 백그라운드로 달력 데이터 로딩 (await 없이) - 월간 구매 있을 때만
        const hasPurchase = hasPurchaseForCategory(catId) || isChoiceSubscriptionCovered.value;
        if (
          currentTab.value === 'choice' &&
          viewTitle.value &&
          mySajuData.value?.guestUserId &&
          hasPurchase
        ) {
          fetchRangeDataForMonth(viewTitle.value, mySajuData.value.guestUserId, catId);
        }
        return;
      }

      // [구독] 커버 월: 구매 없이 달력이 렌더되도록 가상 paidChoiceInfo(집중·월간) 합성
      if (isChoiceSubscriptionCovered.value) {
        ensureSubscriptionPaidItem(catId);
      }

      // 택일 모드 + (월간 구매 OR 구독 커버) 시 달력용 구간 API 호출 (카테고리별 데이터)
      const hasPurchase = hasPurchaseForCategory(catId) || isChoiceSubscriptionCovered.value;
      if (
        currentTab.value === 'choice' &&
        viewTitle.value &&
        mySajuData.value?.guestUserId &&
        hasPurchase
      ) {
        await fetchRangeDataForMonth(viewTitle.value, mySajuData.value.guestUserId, catId);
      }
    }
  }
});

/** [New] 팝업 상태 통합 관리 (Refactoring) */
const {
  isChoicePopupOpen,
  isDailyPopupOpen,
  isChoicePaymentOpen,
  isChoiceConfirmOpen,
  isToastVisible,
  choicePaymentInfo,
  choicePaymentPayload,
  choiceTicketsData,
  choiceTicketCount,
  isTicketLoading,
  choiceConfirmData,
  popupSelectedDate,
  popupInitialView,
  popupData,
  openChoicePopup,
  closeChoicePaymentPopup: _closeChoicePaymentPopup, // 이름 중복 방지
  onCloseChoiceConfirm,
  openDailyPopup,
  setPopupContext,
  reportAvailability,
  hasDayReport,
  hasWeekReport,
  hasMonthReport,
  purchaseType,
  isMultiDayStartDate,
  popupMonthDailyList,
  popupWeekDailyList,
  popupWeekSummary,
  popupMonthSummary,
  popupWeekDateRange,
  popupMonthDateRange,
  popupDailyDetail, // [New]
  analysisOptions,
  fetchAnalysisOptions,
  getChoicePrice,
  // 1일 집중 분석 API 결과
  focus1DetailData,
  isFocus1Loading,
  fetchFocus1Detail,
  findMatchingRangeReport,
  // 구간(7일/30일) 분석 API 결과
  focusRangeData,
  isFocusRangeLoading,
  // [Fix] 카테고리별 캐시
  isCalendarRangeLoading,
  fetchRangeDataForMonth,
  invalidateCalendarCache, // [New] 캐시 무효화 함수
} = useSelectionPopupState({
  currentSajuPaidItems,
  eventByDate, // 이제 useCalendar로부터 선언됨
  categoryMap: CATEGORY_MAP, // [Refactoring] Use centralized categories
  selectionHeaderRef,
  calendarRangeDataCache, // [Fix] 외부에서 선언한 캐시 전달
  currentCalendarCategoryId, // [Fix] 외부에서 선언한 카테고리 ID 전달
});

// [Best Day] 팝업 닫힐 때 bestDay 모드 초기화
watch(isChoicePopupOpen, (newVal) => {
  if (!newVal) {
    bestDaySelection.value = null;
  }
});

// [New] 통합 로딩 상태 (기존 로딩 + 달력 구간 데이터 로딩 + 추천일 로딩)
// [Fix] 팝업이 열려 있을 때는 달력 로딩 인디케이터 숨김 (팝업 내부에서 로딩 처리)
const isCombinedLoading = computed(() => {
  const isAnyPopupOpen = isDailyPopupOpen.value || isChoicePopupOpen.value;
  if (isAnyPopupOpen) {
    // 팝업이 열려 있으면 isCalendarRangeLoading, isBestDayLoading은 무시
    return isLoading.value;
  }
  return isLoading.value || isCalendarRangeLoading.value || isBestDayLoading.value;
});

// [Fix] 택일 탭 전환 시 달력 데이터 로드
// [Fix] oldTab 체크로 초기 로딩 시 중복 호출 방지 (onMounted에서 이미 처리)
watch(currentTab, async (newTab, oldTab) => {
  // 초기 로딩 시(oldTab이 없음)에는 onMounted에서 처리하므로 스킵
  if (!oldTab) return;

  if (newTab === 'choice') {
    // 택일 탭으로 전환 시 월간 구매 있으면 달력 데이터 로드
    const categoryId = currentPurchaseItem.value
      ? CHOICE_CATEGORIES.find((c) => c.name === currentPurchaseItem.value)?.id
      : 'contract';

    // [Fix] 구매 내역을 먼저 조회한 후 Best Day 조회
    await fetchAnalysisHistory(categoryId);

    const tasks = [];
    if (viewTitle.value && mySajuData.value?.guestUserId && hasPurchaseForCategory(categoryId)) {
      tasks.push(fetchRangeDataForMonth(viewTitle.value, mySajuData.value.guestUserId, categoryId));
    }
    // [Best Day] 택일 탭 전환 시 추천일 조회
    tasks.push(fetchBestDay());
    await Promise.all(tasks);
  }
});

/** [New] 현재 달력의 날짜 계산 (Sync with ChoiceStartPopup) */
const currentCalendarDate = computed(() => {
  if (!viewTitle.value) return new Date();
  // viewTitle format: "2024년 1월"
  const match = viewTitle.value.match(/(\d{4})년\s*(\d{1,2})월/);
  if (match) {
    const year = parseInt(match[1]);
    const month = parseInt(match[2]) - 1; // Month is 0-indexed
    return new Date(year, month, 1);
  }
  return new Date();
});

/** [New] 현재 달력 연도/월 (3개월 범위 검증용) */
const currentCalendarYear = computed(() => currentCalendarDate.value.getFullYear());
const currentCalendarMonth = computed(() => currentCalendarDate.value.getMonth() + 1);

// Fetch on mount
onMounted(() => {
  fetchAnalysisOptions();
});
// [추가] 단일 선택 시에도 selectedRange 업데이트 및 팝업 데이터 동기화
watch(selectedDate, (newDate) => {
  if (!newDate) return;

  // 1. 팝업용 날짜 동기화 (기존 900라인 watch 통합)
  popupSelectedDate.value = new Date(newDate);

  // 2. [MODE: Perpetual] 역학달력
  if (currentTab.value !== 'choice') {
    return;
  }

  // 3. [MODE: Choice] 택일 - 단일/Day 모드 대응
  if (selectionMode.value === 'single' || selectionMode.value === 'day') {
    const api = refCalendar.value?.getApi();
    if (api) {
      const dateObj = new Date(newDate);
      const selectEnd = new Date(dateObj);
      selectEnd.setDate(dateObj.getDate() + 1);

      api.unselect();
      api.select(dateObj, selectEnd);
    }
  }
});

// [Refactoring] currentPurchaseItem watcher는 line 837로 통합됨

// [Refactoring] selectionHeaderRef and popup visibility watch moved to useSelectionPopupState

// [Refactoring] popup data generation logic moved to useSelectionPopupState

// [추가] selectedRange 변경 시 달력 리렌더링 (dayCellClassNames 적용 위해)
watch(selectedRange, () => {
  const api = refCalendar.value?.getApi();
  if (api) api.render();
});

// [수정] calendarOptions를 반응형으로 만들기 위해 재정의
const calendarOptions = ref({ ..._calendarOptions });
const isDailyFortuneCalendarOpen = ref(true);

// [Refactoring] popupDateRange logic moved to useSelectionPopupState

// =================== 팝업 및 alert 관리 ===================
// ============================================================
// 📅 [추가] 택일 모드 로직 (1일 / 7일 / 월간)
// ============================================================

// 3. 공통 선택 로직
const applySelection = (api, clickedDate) => {
  // [수정] 기본 단일 모드 (Perpetual) 처리 - useDayCellDidMount 중복 로직 제거 통합
  if (selectionMode.value === 'single') {
    setSelectedDate({ date: clickedDate });
    return;
  }

  // [추가] 클릭 시 기존 단일 선택 해제 (모든 모드 공통)
  selectedDate.value = null;

  // (A) 7일 선택 모드
  if (selectionMode.value === 'week') {
    const endDate = new Date(clickedDate);
    endDate.setDate(clickedDate.getDate() + 6); // 7일 기간 (시작일 포함)

    // [Mod] Week/Month 모드에서는 시작일을 하이라이트에서 제외 (CSS로 별도 처리)
    const highlightStart = new Date(clickedDate);
    highlightStart.setDate(clickedDate.getDate() + 1);

    const selectEnd = new Date(endDate);
    selectEnd.setDate(endDate.getDate() + 1);

    selectedRange.value = { start: clickedDate, end: endDate }; // Ref 먼저 업데이트
    api.select(highlightStart, selectEnd);
  }
  // (B) 월간(전체) 선택 모드
  else if (selectionMode.value === 'month') {
    const year = clickedDate.getFullYear();
    const month = clickedDate.getMonth();
    const monthStart = new Date(year, month, 1);

    // [Mod] 시작일 제외
    const highlightStart = new Date(monthStart);
    highlightStart.setDate(monthStart.getDate() + 1);

    const monthEnd = new Date(year, month + 1, 1);

    const rangeEnd = new Date(year, month + 1, 0);
    selectedRange.value = { start: monthStart, end: rangeEnd }; // Ref 먼저 업데이트
    api.select(highlightStart, monthEnd);
  }
  // (C) [추가] 택일 단일 모드
  else if (selectionMode.value === 'day') {
    const selectEnd = new Date(clickedDate);
    selectEnd.setDate(clickedDate.getDate() + 1);
    // [중요] 기존 선택 해제 후 선택해야 하이라이트가 깔끔하게 갱신됨
    api.unselect();
    api.select(clickedDate, selectEnd);
    selectedRange.value = { start: clickedDate, end: clickedDate };
  }
};

// [New] 우선순위 기반 리포트 판단 로직
const getPriorityReport = (matchingInfos, dayKey, eventByDateArr) => {
  // Helper: 해당 날짜에 day 이벤트 데이터가 있는지 확인
  const hasDayEventData = () => {
    if (!eventByDateArr) return false;
    const dateKey = `${dayKey.slice(0, 4)}-${dayKey.slice(4, 6)}-${dayKey.slice(6, 8)}`; // YYYYMMDD -> YYYY-MM-DD
    const eventInfo = eventByDateArr.find((e) => e.extendedProps?.day === dateKey);
    return eventInfo && eventInfo.extendedProps;
  };

  // 0. 월간(Month) 리포트의 시작일인 경우 -> 최우선으로 월간 팝업
  // Note: paidChoiceInfo의 startDate는 YYYYMMDD 형식 (fetchAnalysisHistory에서 변환됨)
  const monthReport = matchingInfos.find((info) => info.reportType === 'month');
  const isMonthStart = monthReport && monthReport.startDate === dayKey;

  if (isMonthStart) {
    return { type: 'month', report: monthReport };
  }

  // 1. 주간(Week) 리포트의 시작일 체크
  const weekReport = matchingInfos.find((info) => info.reportType === 'week');
  const isWeekStart = weekReport && weekReport.startDate === dayKey;
  const hasMonthOverlap = matchingInfos.some((info) => info.reportType === 'month');

  if (isWeekStart) {
    // [Mod] 월간 겹침 시 → day 데이터 있으면 day 팝업, 없으면 null
    if (hasMonthOverlap) {
      if (hasDayEventData()) {
        return { type: 'day', report: weekReport };
      }
      return null; // 아무것도 열지 않음
    }
    // 월간이 없으면 주간 팝업
    return { type: 'week', report: weekReport };
  }

  // 2. 단일(Day) 리포트가 있는 경우
  const dayReportItem = matchingInfos.find(
    (info) => info.reportType === 'day' || info.reportType === 'single',
  );

  if (dayReportItem) {
    return { type: 'day', report: dayReportItem };
  }

  // 3. 집중 분석(Type 13)이거나 !isLight인 경우
  // 주간/월간 리포트 기간 내라면 시작일이 아니더라도 Day 팝업 오픈
  const concentratedReport = matchingInfos.find(
    (info) =>
      (info.analysisTypeId === 13 || info.isLight === false) &&
      (info.reportType === 'week' || info.reportType === 'month'),
  );

  if (concentratedReport) {
    // [Mod] 월간 겹침 시 → day 데이터 있으면 day 팝업, 없으면 null
    if (hasMonthOverlap) {
      if (hasDayEventData()) {
        return { type: 'day', report: concentratedReport };
      }
      return null;
    }
    return { type: 'day', report: concentratedReport };
  }

  return null;
};

// [추가] 공통 로직: 구매 완료된 날짜 클릭 처리 함수
const handlePaidDateInteraction = async (api, date, jsEvent) => {
  const dayKey = formatDate(date, 'YYYYMMDD');

  // [구독] 커버 월에 카테고리 미선택 상태로 날짜 클릭 → 시작 팝업(구매 플로우) 대신 드롭다운 열고 안내
  if (isChoiceSubscriptionCovered.value && !currentPurchaseItem.value) {
    onChoiceStartCovered();
    return;
  }

  // [New] 해당 날짜에 해당하는 모든 리포트 검색
  let matchingInfos = currentSajuPaidItems.value.filter((item) => {
    const { startDate, endDate } = item;
    return startDate === endDate ? dayKey === startDate : dayKey >= startDate && dayKey <= endDate;
  });

  // [New] 구매 데이터가 없으면 택일시작하기 팝업 열기 (로그인/사주 체크 추가)
  if (matchingInfos.length === 0) {
    // [New] 3개월 범위 검증 - 구매 데이터가 없는 경우에만 체크
    if (!isDateInChoiceRange(date)) {
      const isPast = isDateBeforeRange(date);
      handleDateRangeAlert(isPast ? 'past' : 'future');
      return;
    }

    // 로그인 체크
    if (!userId.value) {
      showLoginConfirm();
      return;
    }
    // 사주 등록 체크
    if (!isMySajuDataExist.value) {
      showRegisterSajuConfirm();
      return;
    }

    // [Best Day] 추천일 클릭 감지
    const isBestDay = bestDayData.value?.date && formatDate(date) === bestDayData.value.date;

    addContentLogAPI({
      contentType: isBestDay ? 'choose_btn_best_day' : 'choose_btn_header_start_opt3',
      option3: isBestDay ? '추천일클릭' : '날짜클릭',
      servicePk: dayKey,
    });
    // 날짜 클릭(무료) — 미구매(분석내역 없음) 날짜 셀 클릭. 집중/라이트는 클릭 시점 미정이라 합산, option3엔 날짜 값.
    addContentLogAPI({
      contentType: 'choose_date_click_free_opt3',
      option3: formatDate(date),
      servicePk: dayKey,
    });

    // [Best Day] 추천일 클릭 → 선택 옵션 프리셋
    if (isBestDay) {
      const bestDay = bestDayData.value;
      bestDaySelection.value = {
        category: bestDay.category,
        reportType: bestDay.reportType || 'day',
        isLight: bestDay.isLight ?? false,
        price: bestDay.price ?? 0,
        date: bestDay.date, // 팝업에서 날짜 프리셋용
        isBestDay: true,
      };
    }

    isChoicePopupOpen.value = true;
    return;
  }

  // [New] 카테고리 필터링
  if (currentPurchaseItem.value) {
    matchingInfos = matchingInfos.filter((item) => item.name === currentPurchaseItem.value);
  }

  // [New] 필터링 후 매칭 없으면 택일시작하기 팝업 열기 (로그인/사주 체크 추가)
  if (matchingInfos.length === 0) {
    // [New] 3개월 범위 검증
    if (!isDateInChoiceRange(date)) {
      const isPast = isDateBeforeRange(date);
      handleDateRangeAlert(isPast ? 'past' : 'future');
      return;
    }

    // 로그인 체크
    if (!userId.value) {
      showLoginConfirm();
      return;
    }
    // 사주 등록 체크
    if (!isMySajuDataExist.value) {
      showRegisterSajuConfirm();
      return;
    }
    // 로그: 날짜 클릭으로 택일시작하기 팝업 열기
    addContentLogAPI({
      contentType: 'choose_btn_header_start_opt3',
      option3: '날짜클릭',
      servicePk: dayKey,
    });
    // 날짜 클릭(무료) — 타 상품만 구매한(현 카테고리 미구매) 날짜 셀 클릭. option3엔 날짜 값.
    addContentLogAPI({
      contentType: 'choose_date_click_free_opt3',
      option3: formatDate(date),
      servicePk: dayKey,
    });
    isChoicePopupOpen.value = true;
    return;
  }

  // 하이라이트 적용
  applySelection(api, date);
  popupSelectedDate.value = date;

  // [Refactoring] 우선순위 로직 실행
  const priority = getPriorityReport(matchingInfos, dayKey, eventByDate.value);
  if (!priority) {
    // 구매 영역 내(분석내역 보유)지만 표시할 result가 없는 날짜(예: 라이트 시작일 외) 클릭 → 유료로 집계
    addContentLogAPI({
      contentType: 'choose_date_click_paid_opt3',
      option3: formatDate(date),
      servicePk: dayKey,
    });
    return;
  }

  // 로그: 결과 팝업 열기
  const getLogOption = (type, isLight) => {
    if (type === 'day') return '1일집중';
    if (type === 'week') return isLight ? '7일라이트' : '7일집중';
    if (type === 'month') return isLight ? '30일라이트' : '30일집중';
    return '1일집중';
  };
  addContentLogAPI({
    contentType: 'choose_popup_result_open_opt3',
    option3: getLogOption(priority.type, priority.report.isLight),
    servicePk: dayKey,
  });
  // 날짜 클릭(유료) — 현재 카테고리로 구매한(분석내역 있음) 날짜 클릭 → 결과 드로어 열림.
  // 집중/라이트 구분은 usage_history에서 analysis_log로 집계하므로 여기 option3엔 날짜 값.
  // (result 없는 구매영역 날짜는 위 !priority 분기에서 이미 동일하게 유료 발화)
  addContentLogAPI({
    contentType: 'choose_date_click_paid_opt3',
    option3: formatDate(date),
    servicePk: dayKey,
  });

  // 팝업 열기
  setPopupContext(date, priority.report.category || priority.report.name);
  popupInitialView.value = priority.type;

  // [Mod] 단일(13) + 구간(14) 분리 요청
  const categoryId = priority.report.category || popupData.value.categoryId;
  // paidChoiceInfo에서 매칭되는 주간/월간 리포트 찾기 (월간 우선)
  // [Note] 월간 데이터는 항상 함께 조회하되, 팝업 초기 탭은 priority.type으로 결정
  const rangeInfo = findMatchingRangeReport(currentSajuPaidItems.value, dayKey, categoryId);

  // [Fix] API 호출을 먼저 시작 (로딩 상태가 설정됨) → 팝업 열기
  // fetchFocus1Detail 내부에서 isFocusRangeLoading = true 설정됨
  const apiPromise = fetchFocus1Detail(mySajuData.value?.guestUserId, date, categoryId, rangeInfo);
  // 팝업은 API 호출 시작 후 바로 열기 (로딩 UI 표시됨)
  isDailyPopupOpen.value = true;
  // API 완료 대기 (선택적 - 팝업 내에서 로딩 상태로 처리됨)
  await apiPromise;
};

// [Fix] 팝업 탭 전환 시 다른 reportType의 구간 데이터 fetch
const TAB_TO_REPORT_TYPE = {
  focus7: 'week',
  focus30: 'month',
  light7: 'week',
  light30: 'month',
};

const handlePopupTabChange = async (tabKey) => {
  const reportType = TAB_TO_REPORT_TYPE[tabKey];
  if (!reportType || !popupSelectedDate.value || !popupData.value?.categoryId) return;

  const dayKey = formatDate(popupSelectedDate.value, 'YYYYMMDD');
  const categoryId = popupData.value.categoryId;
  const rangeInfo = findMatchingRangeReport(
    currentSajuPaidItems.value,
    dayKey,
    categoryId,
    reportType,
  );

  if (rangeInfo) {
    await fetchFocus1Detail(
      mySajuData.value?.guestUserId,
      popupSelectedDate.value,
      categoryId,
      rangeInfo,
    );
  }
};

// ============================================================
// 🎯 [Refactoring] Strategy Pattern Logic Injection
// ============================================================
import { usePCScrollLock } from '@/composables/usePCScrollLock';

// [New] Background Scroll Lock (PC) with Scrollbar Compensation
const isBackgroundScrollLocked = computed(() => {
  return (
    isDailyPopupOpen.value ||
    isChoicePopupOpen.value ||
    isChoicePaymentOpen.value ||
    isChoiceConfirmOpen.value
  );
});

// Use the dedicated composable for scroll locking
usePCScrollLock(isBackgroundScrollLocked);

// ============================================================
// 🎯 [Refactoring] Strategy Pattern Logic Injection
// ============================================================
const perpetualLogic = usePerpetualCalendarLogic({
  setSelectedDate,
});

const choiceLogic = useChoiceCalendarLogic({
  selectionMode,
  setSelectionMode,
  currentPurchaseItem,
  currentSajuPaidItems,
  eventByDate,
  popupSelectedDate,
  popupCategoryName: computed({
    get: () => popupData.value.categoryName,
    set: (val) => {
      popupData.value.categoryName = val;
    },
  }),
  popupCategoryGanji: computed({
    get: () => popupData.value.ganji,
    set: (val) => {
      popupData.value.ganji = val;
    },
  }),
  popupInitialView,
  selectedDate,
  selectedRange,
  setSelectedDate,
  isChoicePopupOpen,
  isChoicePaymentOpen,
  isDailyPopupOpen,
  choicePaymentInfo,
  choicePaymentPayload,
  categoryMap,
  showAlert,
  showConfirm,
  applySelection: (api, date) => applySelection(api, date),
  setPopupContext: (date, category) => setPopupContext(date, category),
  handlePaidDateInteraction: (api, date) => handlePaidDateInteraction(api, date),
});

/** 4. 날짜 클릭 핸들러 */
const onChangeMonth = ({ year, month }) => {
  // month is 1-based, Date needs 0-based
  const newDate = new Date(year, month - 1, 1);
  navigateCalendar({ type: 'goto', date: newDate });
};

const handleDateClick = (info) => {
  if (currentTab.value === 'choice') {
    choiceLogic.handleDateClick(info);
  } else {
    perpetualLogic.handleDateClick(info);
  }
};

/** 5. 이벤트 클릭 핸들러 */
const handleEventClick = (info) => {
  if (currentTab.value === 'choice') {
    choiceLogic.handleEventClick(info);
  } else {
    perpetualLogic.handleEventClick(info);
  }
};

// 4. [중요] FullCalendar 옵션에 클릭 이벤트 연결하기
// useCalendar에서 가져온 옵션에 우리가 만든 기능을 덮어씌웁니다.
// 4. [중요] FullCalendar 옵션에 클릭 이벤트 연결하기
calendarOptions.value = {
  ...calendarOptions.value,
  selectable: false, // [Mod] 드래그 선택 비활성화
  selectMirror: false, // 하이라이트 CSS 적용을 위해 false로 변경 (True면 이벤트 미러링되어 .fc-highlight가 아니라 .fc-event-mirror가 됨)
  unselectAuto: false,
  dateClick: handleDateClick,
  eventClick: handleEventClick, // [추가] 이벤트(글자) 클릭 시에도 동일 동작
  selectOverlap: true, // 선택된 영역 위를 클릭해도 이벤트 발생하도록 허용
  selectAllow: (selectInfo) => {
    // [중요] 달력 뷰(월간) 범위 내의 날짜만 선택 허용 (이전/다음 달 날짜 선택 방지)
    const { start } = selectInfo;
    const view = calendarApi.value?.view;
    if (view && view.type === 'dayGridMonth') {
      return start >= view.currentStart && start < view.currentEnd;
    }
    return true;
  },
  // [추가] 택일 모드 Week/Month 시작일 스타일링
  dayCellClassNames: (arg) => {
    const classList = [];
    if (
      currentTab.value === 'choice' &&
      (selectionMode.value === 'week' || selectionMode.value === 'month') &&
      selectedRange.value?.start
    ) {
      if (isSameDate(arg.date, selectedRange.value.start)) {
        classList.push('choice-start-day-bg');
      }
    }
    // [Best Day] 이달의 추천일 스타일링 (이전/다음 달 날짜는 제외)
    if (
      currentTab.value === 'choice' &&
      !arg.isOther &&
      bestDayData.value?.date &&
      formatDate(arg.date) === bestDayData.value.date
    ) {
      classList.push('best-day-blink');
    }
    return classList;
  },
};

/** 달력 제스처 핸들러 */
const { handleStart, handleMove, handleEnd } = useGesture({
  onSwipeLeft: () => navigateCalendar({ type: 'next' }),
  onSwipeRight: () => navigateCalendar({ type: 'prev' }),
});

/** 4. 사주 설정(케밥 메뉴) 팝업 ID 관리 */
const openSettingId = ref(null);
const toggleSettingPopup = (guestUserId) => {
  if (!guestUserId || !validateMySaju(guestUserId)) return;
  openSettingId.value = openSettingId.value === guestUserId ? null : guestUserId;
};

/** 5. 대표 사주 설정 */
const setMainSetting = async (guestUserId, isDefaultVal) => {
  try {
    const params = {
      guestUserId: guestUserId,
      serviceCategory: 'DAILY_FORTUNE_CALENDAR',
      isDefault: isDefaultVal,
      ...(isDefaultVal && { isActive: true }),
    };
    await setMySajuAPI(params);
    await refreshMySajuList();
    toggleSettingPopup(null);
    triggerSnackbar(isDefaultVal ? `대표 사주로 설정되었습니다.` : `대표 사주가 해제되었습니다.`);
  } catch (e) {
    console.error('대표설정 에러:', e);
  }
};

/** 6. 사주 선택 (활성화) - 컨펌창 띄우기 */
const beforeSetActive = (guestUserId) => {
  if (!guestUserId || !validateMySaju(guestUserId)) return;
  showConfirm({
    title: '선택한 사주로 변경하시겠습니까?',
    text: '확인',
    onConfirm: () => setActiveSetting(guestUserId),
  });
};

/* isActive 선택된 사주 update */
const setActiveSetting = async (guestUserId, showSnackbar = true) => {
  try {
    isLoading.value = true; // 로딩 표시로 UI 깜빡임 방지

    const params = {
      guestUserId: guestUserId,
      serviceCategory: 'DAILY_FORTUNE_CALENDAR',
      isActive: true,
    };
    await setMySajuAPI(params);
    await selectMySaju(guestUserId);
    await refreshMySajuList();

    // 로그: 사주 변경 완료 (팝업 확인 버튼 클릭 후)
    addContentLogAPI({
      contentType: 'lnb_btn_choose_saju_opt3',
      option3: '사주변경',
      servicePk: guestUserId,
    });

    showSnackbar && triggerSnackbar(`선택한 사주로 변경되었습니다.`);
  } catch (e) {
    console.error('내 사주 선택 에러:', e);
  } finally {
    isLoading.value = false;
  }
};

/* 내 사주 그룹 및 내 사주 새로고침 */
const refreshMySajuList = async () => {
  const id = userId.value;
  const yearMonth = convertViewTitleToYYYYMM(viewTitle.value);
  if (id && yearMonth) await fetchMySajuList(id, yearMonth);
};

// [New] Manual Refresh Handler (LNB)
const onManualRefresh = async () => {
  await refreshMySajuList();
  await fetchAnalysisHistory();
};

/** [New] 분석 내역 조회 (API 연결) */
const fetchAnalysisHistory = async (categoryId = null) => {
  if (!userId.value || !mySajuData.value?.guestUserId) return;

  const currentGuestId = mySajuData.value.guestUserId;

  let year, month;

  // [Fix] viewTitle 파싱 ('YYYY년 MM월' -> year, month)
  if (viewTitle.value) {
    const match = viewTitle.value.match(/(\d{4})년\s*(\d{1,2})월/);
    if (match) {
      year = parseInt(match[1]);
      month = parseInt(match[2]);
    }
  }

  // Fallback
  if (!year || !month) {
    const now = new Date();
    year = now.getFullYear();
    month = now.getMonth() + 1;
  }

  // [Fix] 달력에 보이는 전체 날짜 범위 계산 (이전/다음 달 날짜 포함)
  const firstDayOfMonth = new Date(year, month - 1, 1);
  const lastDayOfMonth = new Date(year, month, 0);

  // 달력 시작: 해당 월 1일이 속한 주의 일요일
  const calendarStart = new Date(firstDayOfMonth);
  calendarStart.setDate(1 - firstDayOfMonth.getDay());

  // [Fix] 7일 구매가 이전 달에서 현재 달로 걸쳐있을 수 있으므로 7일 앞당겨서 조회
  calendarStart.setDate(calendarStart.getDate() - 7);

  // 달력 끝: 해당 월 마지막 날이 속한 주의 토요일
  const calendarEnd = new Date(lastDayOfMonth);
  calendarEnd.setDate(lastDayOfMonth.getDate() + (6 - lastDayOfMonth.getDay()));

  // YYYY-MM-DD 포맷으로 변환
  const startDateStr = formatDate(calendarStart, 'YYYY-MM-DD');
  const endDateStr = formatDate(calendarEnd, 'YYYY-MM-DD');

  // Common Params (startDate/endDate 사용)
  const baseParams = {
    userId: userId.value,
    guestUserId: currentGuestId,
    includeChildTypes: false,
    isValid: true,
    startDate: startDateStr,
    endDate: endDateStr,
    page: 1,
    size: -1,
  };

  // [New] Apply category filter if provided
  if (categoryId) {
    baseParams.category = categoryId;
  }

  try {
    // Fetch Concentrated (13) with includeChildTypes to get Light (14) as well
    const result = await getAnalysisLogAPI({
      ...baseParams,
      analysisTypeId: 13,
      includeChildTypes: true,
    });

    const allContent = result?.content || [];

    if (allContent.length > 0) {
      const newItems = allContent.map((item) => {
        const sStr = item.startDate.replaceAll('-', '');
        const eStr = item.endDate.replaceAll('-', '');

        let rType = 'day';

        // [Fix] Use API ranges
        if (item.monthRange && item.monthRange >= 1) rType = 'month';
        else if (item.dayRange && item.dayRange >= 7) rType = 'week';
        else rType = 'day';

        return {
          name: CATEGORY_MAP[item.category] || item.category,
          category: item.category,
          reportType: rType,
          startDate: sStr,
          endDate: eStr,
          guestUserId: currentGuestId,
          analysisTypeId: item.analysisTypeId,
          analysisLogId: item.analysisLogId,
          isLight: item.analysisTypeId === 14, // [Fix] AnalysisType 14 is Light
        };
      });

      // Deduplicate and append/update
      newItems.forEach((newItem) => {
        const index = paidChoiceInfo.value.findIndex(
          (p) =>
            p.guestUserId === newItem.guestUserId &&
            p.startDate === newItem.startDate &&
            p.category === newItem.category &&
            p.reportType === newItem.reportType &&
            (p.isLight ?? p.name?.includes('라이트')) ===
              (newItem.isLight ?? newItem.name?.includes('라이트')),
        );

        if (index !== -1) {
          // Update existing item (especially for isLight flag fix)
          paidChoiceInfo.value[index] = { ...paidChoiceInfo.value[index], ...newItem };
        } else {
          paidChoiceInfo.value.push(newItem);
        }
      });
    }
  } catch (e) {
    console.error('Failed to fetch analysis history:', e);
  }
};

// [New] 상태 복원을 위한 변수
const initialChoiceCategory = ref(null);
const initialChoiceReport = ref(null);

const fetchUserStoreData = () => {
  paymentsStore.fetchPointsAndTickets();
  primarySajuStore.fetchPrimaryAndMySaju();
};

onMounted(async () => {
  // [구독] 택일 구독 조회 판별에 쓸 활성 구독 상태 로드 (isSubscribed 신뢰 확보, TTL 가드 있음)
  paymentsStore.fetchActiveSubscriptions();

  // 모바일 진입 로그 — 모바일은 LNB가 없어 진입 시점에 현재 탭 기준으로 발화 (탭 전환 발화와 동일 type)
  if (isMobile.value) {
    addContentLogAPI({
      contentType: 'lnb_btn_calendar_opt3',
      option3: currentTab.value === 'choice' ? '택일달력' : '역학달력',
    });
  }

  // [New] URL 쿼리 파라미터 확인 (충전 후 복귀 시)
  const { openStartPopup, category, report } = route.query;
  if (openStartPopup === 'true') {
    initialChoiceCategory.value = category;
    initialChoiceReport.value = report;
    isChoicePopupOpen.value = true;

    // 쿼리 파라미터 정리 (선택사항, 깔끔하게 보이려면)
    // router.replace({ query: {} });
  }

  // 1) 쿼리 파라미터 우선순위 확인
  // [초기화] 새로고침 시 초기 탭에 맞춰 상태(필터 등) 설정
  if (currentTab.value !== 'choice') {
    // 외부 진입(GNB/새로고침) 시 역학달력이면 모든 옵션 ON
    calendarFilter.isKorChecked.value = true;
    calendarFilter.isColorChecked.value = true;
  }
  updateSelectionModeByTab(currentTab.value);

  if (userId.value) fetchUserStoreData();

  // [Fix] 페이지 재진입 시 택일 모드 데이터 로드
  // watch는 값이 "변경"될 때만 트리거되므로, 이미 값이 있는 상태에서 재진입하면 호출되지 않음
  if (currentTab.value === 'choice' && viewTitle.value && mySajuData.value?.guestUserId) {
    const categoryId = currentPurchaseItem.value
      ? CHOICE_CATEGORIES.find((c) => c.name === currentPurchaseItem.value)?.id
      : 'contract';
    // 구매 내역 먼저 조회 후 구간 데이터 로드
    await fetchAnalysisHistory(categoryId);
    const hasPurchase = hasPurchaseForCategory(categoryId);
    const tasks = [];
    if (hasPurchase) {
      tasks.push(fetchRangeDataForMonth(viewTitle.value, mySajuData.value.guestUserId, categoryId));
    }
    // [Best Day] 페이지 진입 시 추천일 조회
    tasks.push(fetchBestDay());
    await Promise.all(tasks);
  }
});

// [Optimized] viewTitle/guestUserId 변경 통합 watcher
watch(
  [viewTitle, () => mySajuData.value?.guestUserId],
  async ([newTitle, newGuestId], [oldTitle, oldGuestId]) => {
    // viewTitle 변경 시: 사주 목록 + 히스토리 조회 + 달력용 구간 API 호출
    if (newTitle && newTitle !== oldTitle) {
      await refreshMySajuList();
      await fetchAnalysisHistory();

      // 택일 모드 + 해당 카테고리에 월간 구매 있으면 달력 표시용 구간 API 호출
      if (currentTab.value === 'choice' && newGuestId) {
        const categoryId = currentPurchaseItem.value
          ? CHOICE_CATEGORIES.find((c) => c.name === currentPurchaseItem.value)?.id
          : 'contract';

        // [구독] 월 변경 시 선택 유지(B): 새 달 접근권을 확정(await)하고, 커버면 같은 카테고리로 합성
        //  - await 라 새 달 canAccess가 캐시든 신규든 확정된 뒤 진행 (비동기 타이밍 해결)
        let covered = false;
        const ym = viewYearMonth.value;
        if (
          currentPurchaseItem.value &&
          ym &&
          paymentsStore.isSubscribed &&
          primarySajuStore.isMySaju(Number(newGuestId))
        ) {
          covered = await fetchChoiceAccess(newGuestId, ym.year, ym.month);
          if (covered) ensureSubscriptionPaidItem(categoryId);
        }

        const hasPurchase = hasPurchaseForCategory(categoryId) || covered;
        const tasks = [];
        if (hasPurchase) {
          tasks.push(fetchRangeDataForMonth(newTitle, newGuestId, categoryId));
        }
        // [Best Day] 월 변경 시 추천일 조회
        tasks.push(fetchBestDay());
        await Promise.all(tasks);
      }
    }
    // guestUserId만 변경 시: 히스토리만 조회
    // [Fix] fetchRangeDataForMonth는 watch(currentPurchaseItem)에서 처리
    // (fetchAnalysisHistory → currentSajuPaidItems → purchaseList → currentPurchaseItem 자동 설정 → watch 트리거)
    else if (newGuestId && newGuestId !== oldGuestId) {
      await fetchAnalysisHistory();

      if (currentTab.value === 'choice' && viewTitle.value) {
        fetchBestDay();
      }
    }
  },
);

// [Best Day] 구매 이력 변경 시 현재 달에 구매가 생기면 bestDayData 클리어
watch(currentSajuPaidItems, () => {
  if (hasCurrentMonthPurchase(viewTitle.value)) {
    bestDayData.value = null;
  }
});

onMounted(() => {
  nextTick(() => {
    syncMobileLayoutChrome();
    syncOverscrollBehavior();
  });
});

watch(isMobile, () => {
  nextTick(() => {
    syncMobileLayoutChrome();
    syncOverscrollBehavior();
  });
});

watch(userId, async (newId, oldId) => {
  if (newId && !oldId) {
    await refreshMySajuList();
    fetchUserStoreData();
  } else if (!newId && oldId) {
    clearMySajuState();
  }
});

useBackLayer({
  id: 'selection-option-drawer',
  isOpen: isDrawerOpen,
  closeFromBack: () => {
    isDrawerOpen.value = false;
  },
});
useBackLayer({
  id: 'selection-saju-sidebar',
  isOpen: isMobileSidebarOpen,
  closeFromBack: closeMobileSidebar,
});
useBackLayer({
  id: 'selection-daily-result',
  isOpen: isDailyPopupOpen,
  closeFromBack: () => {
    isDailyPopupOpen.value = false;
  },
});
useBackLayer({
  id: 'selection-choice-start',
  isOpen: isChoicePopupOpen,
  closeFromBack: () => {
    isChoicePopupOpen.value = false;
  },
});
useBackLayer({
  id: 'selection-payment',
  isOpen: isChoicePaymentOpen,
  closeFromBack: handleCloseChoicePaymentPopup,
});
useBackLayer({
  id: 'selection-payment-confirm',
  isOpen: isChoiceConfirmOpen,
  closeFromBack: handleCloseChoiceConfirm,
});
useBackLayer({
  id: 'selection-saju-clock',
  isOpen: isSajuClockOpen,
  closeFromBack: () => setSajuClockOpen(false),
});
useBackLayer({
  id: 'selection-usage-guide',
  isOpen: isOpenPopSelectionUsage,
  closeFromBack: () => {
    isOpenPopSelectionUsage.value = false;
  },
});
useBackLayer({
  id: 'selection-date-toast',
  isOpen: isToastVisible,
  closeFromBack: () => {
    isToastVisible.value = false;
  },
});

onUnmounted(() => {
  if (typeof document !== 'undefined') {
    const layoutEl = document.querySelector('.manse-layout');
    layoutEl?.classList.remove('selection-mobile-mode');
    document.documentElement.style.overscrollBehavior = '';
  }
});
</script>

<template>
  <div class="calendar-wrap" :class="isMobile ? 'mobile' : 'v2'">
    <MobileHeader
      v-if="showMobileHeader"
      title="택일달력"
      :show-back="isWebView"
      :show-menu="!isWebView"
      @back="goBack"
    />

    <!-- PC: CreditBar -->
    <CreditBar v-if="!isMobile" :userId="userId" />

    <!-- 모바일: ToggleDrawer (역학달력 옵션) -->
    <ToggleDrawer
      v-if="isMobile"
      v-show="isDrawerOpen"
      v-model="isDrawerOpen"
      :toggleOptions="calendarFilter.toggleOptions"
      @close="isDrawerOpen = false"
    />

    <!-- 모바일: 사이드바 Drawer -->
    <MobileCalendarDrawer v-if="isMobile" v-model="isMobileSidebarOpen">
      <MobileCalendarDrawerHeader
        :selectedTab="selectedSidebarTab"
        @select="(tab) => (selectedSidebarTab = tab)"
        @close="closeMobileSidebar"
      />
      <MobileCalendarDrawerBody
        :selectedSidebarTab="selectedSidebarTab"
        :userId="userId"
        :paymentsStore="paymentsStore"
        :isLoading="isCombinedLoading"
        :isSajuDataExist="isMySajuDataExist"
        :isDailyFortuneCalendarOpen="true"
        :mySajuObject="parsedMySajuInfo"
        :mySajuList="mySajuList"
        :mySajuMonthlyStatus="null"
        :sajuganjiArray="parsedMySajuInfo?.sajuganjiArray"
        :sajuganjiKorArray="parsedMySajuInfo?.sajuganjiKorArray"
        :viewTitle="viewTitle"
        :openSettingId="openSettingId"
        :DEV_MODE="DEV_MODE"
        :monthlyFortunePrice="0"
        viewingLabel="택일 보는 중"
        :hideMonthlyBadge="true"
        :showTaegilBadge="true"
        serviceName="택일"
        :primarySajuId="primarySajuStore.primarySajuId"
        :mySajuId="primarySajuStore.mySajuId"
        @refreshMySajuList="onManualRefresh"
        @handleGoManse="() => router.push('/saju-input')"
        @setMainSetting="setMainSetting"
        @toggleSettingPopup="toggleSettingPopup"
        @setActiveSetting="beforeSetActive"
        @openPopHowto="() => {}"
        @openPopUsage="isOpenPopSelectionUsage = true"
        @goLogin="showLoginConfirm"
      />
    </MobileCalendarDrawer>

    <div
      class="content-main"
      :class="{ 'mb-large': !isMobile, 'with-mobile-header': showMobileHeader, webview: isWebView }"
    >
      <section
        :class="[
          isMobile ? 'inner-base' : 'inner-base-calendar v2',
          { 'choice-mode': currentTab === 'choice' },
        ]"
      >
        <div v-if="!isMobile" style="height: 20px"></div>
        <div class="calendar-sidebar-layout">
          <!-- PC: SelectionLnb (사이드바) -->
          <SelectionLnb
            v-if="!isMobile"
            :toggleOptions="calendarFilter.toggleOptions"
            :sajuData="mySajuData"
            :isSajuDataExist="isMySajuDataExist"
            :mySajuObject="parsedMySajuInfo"
            :mySajuList="mySajuList"
            :openSettingId="openSettingId"
            :primarySajuId="primarySajuStore.primarySajuId"
            :mySajuId="primarySajuStore.mySajuId"
            @setMainSetting="setMainSetting"
            @setActiveSetting="beforeSetActive"
            @refreshMySajuList="onManualRefresh"
            @toggleSettingPopup="toggleSettingPopup"
            @update:tabValue="handleTabUpdate"
            @selectMySaju="selectMySaju"
            :currentTab="currentTab"
            :isDisabled="false"
            viewingLabel="택일 보는 중"
          />

          <div class="calendar-main">
            <!-- PC: SelectionHeader -->
            <SelectionHeader
              v-if="!isMobile"
              ref="selectionHeaderRef"
              :calendar-type="'v2'"
              :title="viewTitle"
              :is-today="isToday"
              :solar-term="firstSeason"
              :solar-term-month="firstSeasonMonth"
              :is-color-checked="calendarFilter.isColorChecked.value"
              :current-tab="currentTab"
              :user-id="userId"
              :is-saju-data-exist="isMySajuDataExist"
              :purchase-list="purchaseList"
              v-model:selected-item="currentPurchaseItem"
              :is-disabled="false"
              :is-list-locked="false"
              :is-subscription-covered="isChoiceSubscriptionCovered"
              @prev="navigateCalendar({ type: 'prev' })"
              @next="navigateCalendar({ type: 'next' })"
              @today="navigateCalendar({ type: 'today' })"
              @clock="setSajuClockOpen(true)"
              @update:selection-mode="setSelectionMode"
              @req-login="showLoginConfirm"
              @req-register-saju="showRegisterSajuConfirm"
              @req-start-selection="onReqStartSelection"
              @show-date-range-alert="handleDateRangeAlert"
              @toggle-test-data="toggleTestData"
              @change-month="onChangeMonth"
              :current-calendar-year="currentCalendarYear"
              :current-calendar-month="currentCalendarMonth"
            />

            <!-- 모바일: 역학달력 헤더 -->
            <CalendarHeaderMobile
              v-if="isMobile && currentTab === 'perpetual'"
              :calendarType="'mobile'"
              :title="viewTitle"
              :isToday="isToday"
              :solarTerm="firstSeason"
              :solarTermMonth="firstSeasonMonth"
              :isColorChecked="calendarFilter.isColorChecked"
              @prev="navigateCalendar({ type: 'prev' })"
              @next="navigateCalendar({ type: 'next' })"
              @today="navigateCalendar({ type: 'today' })"
              @clock="setSajuClockOpen(true)"
              @option="isDrawerOpen = true"
            />

            <!-- 모바일: 맞춤택일 헤더 -->
            <MobileSelectionHeader
              v-if="isMobile && currentTab === 'choice'"
              ref="selectionHeaderRef"
              :viewTitle="viewTitle"
              :currentPurchaseItem="currentPurchaseItem"
              :uniquePaidItems="uniquePaidItems"
              :current-calendar-year="currentCalendarYear"
              :current-calendar-month="currentCalendarMonth"
              :is-subscription-covered="isChoiceSubscriptionCovered"
              @goBack="goBack"
              @prev="navigateCalendar({ type: 'prev' })"
              @next="navigateCalendar({ type: 'next' })"
              @change-month="onChangeMonth"
              @selectPurchaseItem="handleSelectPurchaseItem"
              @openMySajuDrawer="openMySajuDrawerMobile"
              @openChoicePopup="handleMobileOpenChoicePopup"
              @show-date-range-alert="handleDateRangeAlert"
            />

            <!-- 모바일: 탭바 (역학달력/맞춤택일) -->
            <MobileSelectionTabBar
              v-if="isMobile"
              :currentTab="currentTab"
              @update:currentTab="handleMobileTabChange"
            />

            <div
              v-if="isMobile"
              class="sec-sch-body mobile with-bottom-tab"
              :class="{ 'with-mobile-header': showMobileHeader }"
            >
              <v-row
                no-gutters
                class="fill-height"
                @mousedown="handleStart"
                @mousemove="handleMove"
                @mouseup="handleEnd"
                @touchstart="handleStart"
                @touchmove="handleMove"
                @touchend="handleEnd"
              >
                <v-col>
                  <v-sheet
                    :height="isMobile ? undefined : 700"
                    :style="isMobile ? {} : { position: 'relative' }"
                  >
                    <FullCalendar
                      ref="refCalendar"
                      class="full-calendar mobile"
                      :options="calendarOptions"
                    />
                  </v-sheet>
                </v-col>
              </v-row>
            </div>
            <div v-else class="perpetual-content-body">
              <v-row
                no-gutters
                class="fill-height"
                @mousedown="handleStart"
                @mousemove="handleMove"
                @mouseup="handleEnd"
                @touchstart="handleStart"
                @touchmove="handleMove"
                @touchend="handleEnd"
              >
                <v-col>
                  <v-sheet height="700" style="position: relative">
                    <FullCalendar
                      ref="refCalendar"
                      class="full-calendar web"
                      :options="calendarOptions"
                    />
                  </v-sheet>
                </v-col>
              </v-row>
            </div>
          </div>
        </div>
      </section>
    </div>

    <SajuClockPopup
      v-if="isSajuClockOpen"
      ref="sajuClockRef"
      @closeClock="setSajuClockOpen(false)"
    />

    <!-- [New] 탭 전환 및 가용성 확인 로직 -->
    <!-- 팝업 통합: ChoiceWeekPopup 제거, ChoiceDayPopup이 두 역할 모두 수행 -->
    <ChoiceDatePopup
      v-model:isOpen="isDailyPopupOpen"
      :selectedDate="popupSelectedDate"
      :initialView="popupInitialView"
      :categoryName="popupData.categoryName"
      :ganji="popupData.ganji"
      :isMobile="isMobile"
      :hasDayReport="hasDayReport"
      :hasWeekReport="hasWeekReport"
      :hasMonthReport="hasMonthReport"
      :purchaseType="purchaseType"
      :isMultiDayStartDate="isMultiDayStartDate"
      :monthDailyList="popupMonthDailyList"
      :weekDailyList="popupWeekDailyList"
      :monthSummary="popupMonthSummary"
      :weekSummary="popupWeekSummary"
      :weekDateRange="popupWeekDateRange"
      :categoryId="popupData.categoryId"
      :monthDateRange="popupMonthDateRange"
      :dailyDetail="popupDailyDetail"
      :focusDetailData="focus1DetailData"
      :isFocus1Loading="isFocus1Loading"
      :focusRangeData="focusRangeData"
      :isFocusRangeLoading="isFocusRangeLoading"
      @tab-change="handlePopupTabChange"
    />
    <ChoiceStartPopup
      v-model:isOpen="isChoicePopupOpen"
      :initial-category="popupData.categoryId"
      :initial-ganji="popupData.ganji"
      :user-points="paymentsStore.formattedPoints"
      :paid-items="paidChoiceInfo"
      :user-id="mySajuData?.guestUserId"
      :parent-user-id="userId"
      :is-mobile="isMobile"
      :pricing-helper="getChoicePrice"
      :initial-date="currentCalendarDate"
      :is-best-day="!!bestDaySelection"
      :initial-selection="bestDaySelection"
      @select-complete="handleSelectionComplete"
      @req-charge="handleReqCharge"
    />

    <!-- [New] 택일 결제 팝업 -->
    <ChoicePaymentPopup
      :isVisible="isChoicePaymentOpen"
      :choiceInfo="choicePaymentInfo"
      :payload="choicePaymentPayload"
      :ticketCountData="choiceTicketsData"
      :ticketCount="choiceTicketCount"
      :isMobile="isMobile"
      @close="handleCloseChoicePaymentPopup"
      @payment-success="handleChoicePaymentSuccess"
    />

    <AlertComponent
      ref="alertRef"
      :title="dialogTitle"
      :titleClass="dialogTitleClass"
      :title2="dialogTitle2"
      :text="dialogText"
    />

    <ConfirmComponent
      ref="confirmRef"
      :title="dialogTitle"
      :titleClass="dialogTitleClass"
      :title2="dialogTitle2"
      :title2Class="dialogTitle2Class"
      :message="dialogMessage"
      :text="dialogText"
      :nextUrl="dialogNextUrl"
      :cancelText="dialogCancelText"
      :cancelUrl="dialogCancel"
    />

    <!-- [New] Choice Confirm Popup -->
    <ChoiceConfirmPopup
      :isVisible="isChoiceConfirmOpen"
      :category="choiceConfirmData.category"
      :itemName="choiceConfirmData.itemName"
      :dateText="choiceConfirmData.dateText"
      @close="handleCloseChoiceConfirm"
      @confirm="onConfirmChoice"
    />

    <!-- [추가] 날짜 선택 유도 토스트 팝업 -->
    <ToastDatePopup :isVisible="isToastVisible" @close="isToastVisible = false" />

    <!-- [반응형] 모바일 택일 사용법 가이드 팝업 -->
    <PopSelectionUsageMobile
      v-if="isMobile && isOpenPopSelectionUsage"
      @close="isOpenPopSelectionUsage = false"
    />

    <Snackbar ref="snackbarRef" />

    <LoadingIndicator v-if="isCombinedLoading" />
  </div>
</template>

<style lang="scss">
.manse-layout.selection-mobile-mode > header,
.manse-layout.selection-mobile-mode > footer {
  display: none !important;
}

.manse-layout.selection-mobile-mode > .manse-main {
  padding-top: var(--safe-area-top, 0px) !important;
}
</style>

<style lang="scss" scoped>
/* 버튼 스타일 */

/* ✨ FullCalendar 스타일 오버라이드 
  scoped 스타일 안에서 자식 컴포넌트(FullCalendar)의 내부 클래스를 바꾸려면
  :deep() 또는 ::v-deep을 사용해야 합니다.
*/
:deep(.fc-highlight) {
  /* 기본 모드 (역학달력) 스타일 */
  background: rgba(235, 76, 16, 0.05) !important;
  border: 2px solid #eb4c10 !important;
  border-radius: 12px !important;
  opacity: 1 !important;
  position: absolute;
  z-index: 2 !important;
  pointer-events: none;
}

/* 택일 모드 스타일 오버라이드 */
.choice-mode :deep(.fc-highlight) {
  background: #fffaf2 !important;
  border: none !important;
  border-radius: 0 !important;
  opacity: 1 !important;
  mix-blend-mode: multiply !important;
  z-index: 0 !important;
}

/* 택일 모드: 날짜 숫자 위의 동그라미 하이라이트 제거 */
.choice-mode :deep(.custom-day-selected) {
  background-color: transparent !important;
}

/* [추가] 택일 모드 시작일 배경색 */
.choice-mode :deep(.choice-start-day-bg .fc-daygrid-day-frame) {
  background-color: #fff3de !important;
}

/* [반응형] 모바일 달력 body — 부모 스크롤 모드 */
.sec-sch-body.mobile.with-bottom-tab {
  overflow: visible;
  height: auto; /* 글로벌 .sec-sch-body.mobile { height: 100% } 해제 */
}

.sec-sch-body.mobile.with-bottom-tab :deep(.full-calendar.mobile) {
  height: auto;
}

/* fill-height 해제 — 부모 스크롤 모드에서 불필요 */
.sec-sch-body.mobile.with-bottom-tab :deep(.v-row.fill-height) {
  height: auto;
}

/* FullCalendar 내부 스크롤 → 부모 스크롤 전환 */
.sec-sch-body.mobile.with-bottom-tab :deep(.fc-scroller.fc-scroller-liquid-absolute) {
  position: relative !important;
  overflow: hidden !important;
  width: 100%;
}

.sec-sch-body.mobile.with-bottom-tab :deep(.fc-view-harness) {
  flex-grow: 0;
  height: auto !important;
  min-block-size: auto;
}

/* FullCalendar view: absolute → relative (부모 높이에 반영되도록) */
.sec-sch-body.mobile.with-bottom-tab :deep(.fc-view-harness > .fc-view) {
  position: relative !important;
  inset: auto !important;
}

/* FullCalendar liquid section → auto height (부모 스크롤 모드) */
.sec-sch-body.mobile.with-bottom-tab :deep(.fc-scrollgrid-section-liquid > td) {
  height: auto;
}

.calendar-wrap.mobile {
  background: #fff !important;
  height: auto;

  // MobileHeader를 fixed → sticky로 전환하여
  // CalendarHeader(sticky)와 동일하게 바운스 시 함께 움직이도록 처리
  :deep(.mobile-header) {
    position: sticky !important;
  }
  // sticky는 document flow에 포함되므로 margin-top 불필요
  .content-main.with-mobile-header {
    margin-top: 0;
  }

  // 달력 셀 최소 높이 (낭만일진 calendar_mobile.scss와 동일 방식)
  :deep(.fc-scrollgrid-sync-table) {
    height: auto !important;
  }
  :deep(.fc-daygrid-day) {
    height: 140px;
  }
  // calendar_mobile.scss의 .fc-scrollgrid { margin-bottom: 60px } 해제
  // 택일 페이지는 부모 스크롤 모드이므로 CalendarBottomLabel 여백 불필요
  :deep(.fc-scrollgrid) {
    margin-bottom: 0;
  }
}

.calendar-wrap.mobile > .content-main {
  height: auto;
  min-height: 0 !important;
}

/* [반응형] 모바일 달력 헤더 sticky 고정 */
.calendar-wrap.mobile .calendar-main {
  // 역학달력 헤더 / 맞춤택일 헤더
  :deep(.schedule-year-box),
  :deep(.choice-header-mobile) {
    position: sticky;
    top: var(--safe-area-top, 0px); // MobileHeader 제거됨 (하단 탭바로 대체)
    z-index: 10;
    background: #fff;
  }
}

/* 요일 헤더(일~토) sticky 고정 — fc overflow 해제 필요 */
.sec-sch-body.mobile.with-bottom-tab :deep(.fc) {
  overflow: visible;
}
.sec-sch-body.mobile.with-bottom-tab :deep(.fc-scrollgrid > thead) {
  position: sticky;
  top: calc(var(--safe-area-top, 0px) + 48px); // 월 네비(48px) 아래 — MobileHeader 제거됨(탭바로 대체)
  z-index: 5;
  background: #fff;
  box-shadow: 0 1px 0 #ddd; // border-collapse: separate에서 thead border 대체
}
</style>
